<template>
  <div class="an-root">
    <!-- ── Top bar ─────────────────────────────────────────────────────── -->
    <header class="an-topbar no-print">
      <div class="brand">
        <button class="back-btn" @click="goBack" title="Back to dashboard"><v-icon size="20" color="#14663a">mdi-arrow-left</v-icon></button>
        <div class="brand-logo-box"><img :src="wtiLogo" class="brand-logo" alt="WTI" /></div>
        <div class="brand-text">
          <div class="brand-title">Executive Comparisons &amp; Analytics</div>
          <div class="brand-sub">Wildlife Trust of India · Strategic Executive Suite</div>
        </div>
      </div>
      <div class="topbar-actions">
        <!-- Dynamic Financial Year Selector -->
        <div class="fy-picker" v-if="financialYearList.length">
          <v-icon size="16" color="#14663a">mdi-calendar-range</v-icon>
          <select v-model="selectedFY" class="fy-select" @change="onFYChange">
            <option v-for="fy in financialYearList" :key="fy" :value="fy">FY {{ fy }}</option>
          </select>
        </div>
        <span class="fy-chip" v-else-if="financialYear">FY {{ financialYear }}</span>

        <!-- Board-Ready Export -->
        <button class="btn ghost export-btn" @click="exportReport" title="Print or save current briefing as PDF">
          <v-icon size="17" color="#14663a">mdi-printer-outline</v-icon>
          <span>Export PDF</span>
        </button>

        <button class="btn ghost" :disabled="loading" @click="fetchAllData"><v-icon size="17" :class="{ spin: loading }">mdi-refresh</v-icon><span>Refresh</span></button>
        <button class="btn solid" @click="logout"><v-icon size="17">mdi-logout</v-icon><span>Logout</span></button>
      </div>
    </header>

    <!-- ── Main Navigation Tabs ────────────────────────────────────────── -->
    <nav class="an-nav no-print">
      <div class="nav-tabs">
        <button class="nav-tab" :class="{ active: activeTab === 'scorecards' }" @click="switchTab('scorecards')">
          <v-icon size="18" :color="activeTab === 'scorecards' ? '#14663a' : '#6b7a72'">mdi-folder-account-outline</v-icon>
          <span>Division &amp; Team Scorecards</span>
        </button>
        <button class="nav-tab" :class="{ active: activeTab === 'pip_comparison' }" @click="switchTab('pip_comparison')">
          <v-icon size="18" :color="activeTab === 'pip_comparison' ? '#14663a' : '#6b7a72'">mdi-clipboard-pulse-outline</v-icon>
          <span>PIP Comparisons</span>
          <span class="badge" v-if="pipReady">{{ pipDivisions.length }}</span>
        </button>
        <button class="nav-tab" :class="{ active: activeTab === 'talent_matrix' }" @click="switchTab('talent_matrix')">
          <v-icon size="18" :color="activeTab === 'talent_matrix' ? '#14663a' : '#6b7a72'">mdi-grid</v-icon>
          <span>Talent 9-Box Matrix</span>
          <span class="badge" v-if="talentData && talentData.totalEmployees">{{ talentData.totalEmployees }}</span>
        </button>
        <button class="nav-tab" :class="{ active: activeTab === 'projects' }" @click="switchTab('projects')">
          <v-icon size="18" :color="activeTab === 'projects' ? '#14663a' : '#6b7a72'">mdi-compass-outline</v-icon>
          <span>Project Benchmarking</span>
          <span class="badge" v-if="projectList.length">{{ projectList.length }}</span>
        </button>
        <button class="nav-tab" :class="{ active: activeTab === 'donations_yoy' }" @click="switchTab('donations_yoy')">
          <v-icon size="18" :color="activeTab === 'donations_yoy' ? '#14663a' : '#6b7a72'">mdi-chart-timeline-variant</v-icon>
          <span>Multi-Year YoY Donations</span>
        </button>
      </div>
    </nav>

    <!-- Print Header -->
    <div class="print-header print-only">
      <div class="print-brand">
        <img :src="wtiLogo" class="print-logo" alt="WTI" />
        <div>
          <div class="print-title">Wildlife Trust of India — Executive Analytics Report</div>
          <div class="print-sub">Generated for Executive Leadership · Financial Year: {{ selectedFY || financialYear }}</div>
        </div>
      </div>
      <div class="print-date">{{ printDate }}</div>
    </div>

    <div v-if="loading" class="loader-wrap"><v-progress-circular indeterminate color="#14663a" size="52" width="4" /></div>

    <main v-else class="an-body">
      <div v-if="error" class="banner error"><v-icon size="18" color="#c62828">mdi-alert-circle-outline</v-icon> {{ error }}</div>

      <section class="executive-scope">
        <div class="scope-intro"><span>Executive comparison scope</span><strong>FY {{ selectedFY || financialYear }}</strong></div>
        <div class="scope-item"><span>KPI scorecards</span><strong>{{ num(a.adoption.employees) }} staff</strong><small>Employees with target sheets</small></div>
        <div class="scope-item"><span>PIP evidence</span><strong>{{ pipReady ? num(pipSummary.mappedEmployees) + ' staff' : 'Unavailable' }}</strong><small>{{ pipScopeLabel }}</small></div>
        <div class="scope-item"><span>PMR appraisal</span><strong>{{ pmrAvailable ? 'Included' : 'Unavailable' }}</strong><small>Used for talent potential</small></div>
        <div class="scope-item"><span>Project benchmark</span><strong>{{ num(projectList.length) }} projects</strong><small>Active project mappings</small></div>
      </section>

      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <!-- TAB 1: DIVISION & TEAM SCORECARDS                                   -->
      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <div v-if="activeTab === 'scorecards'">
        <!-- Adoption Funnel -->
        <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-progress-check</v-icon> Target Process Adoption</div>
        <div v-if="a.adoption.submissionRate < 25" class="insight no-print">
          <v-icon size="18" color="#b56b00">mdi-alert</v-icon>
          <span>Only <b>{{ a.adoption.submissionRate }}%</b> of assigned targets have been submitted. KPI sheets cover <b>{{ a.adoption.employees }}</b> of <b>{{ activeEmployeeCount }}</b> active employees ({{ kpiCoverageRate }}%), so KPI-led scores remain provisional where coverage is incomplete.</span>
        </div>
        <section class="funnel">
          <div class="fn-step"><div class="fn-v">{{ num(activeEmployeeCount) }}</div><div class="fn-l">Active employees</div></div>
          <v-icon size="20" color="#c9d6ce" class="no-print">mdi-chevron-right</v-icon>
          <div class="fn-step"><div class="fn-v">{{ num(a.adoption.employees) }}</div><div class="fn-l">KPI sheets <em>{{ kpiCoverageRate }}%</em></div></div>
          <v-icon size="20" color="#c9d6ce" class="no-print">mdi-chevron-right</v-icon>
          <div class="fn-step"><div class="fn-v">{{ num(a.adoption.assigned) }}</div><div class="fn-l">Targets assigned</div></div>
          <v-icon size="20" color="#c9d6ce" class="no-print">mdi-chevron-right</v-icon>
          <div class="fn-step"><div class="fn-v">{{ num(a.adoption.submitted) }}</div><div class="fn-l">Submitted <em>{{ a.adoption.submissionRate }}%</em></div></div>
          <v-icon size="20" color="#c9d6ce" class="no-print">mdi-chevron-right</v-icon>
          <div class="fn-step"><div class="fn-v">{{ num(a.adoption.approved) }}</div><div class="fn-l">Approved <em>{{ a.adoption.approvalRate }}%</em></div></div>
          <div class="fn-step warn"><div class="fn-v">{{ num(a.adoption.notStarted) }}</div><div class="fn-l">Not started (0 submitted)</div></div>
        </section>

        <!-- Division Scorecards -->
        <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-folder-multiple</v-icon> Division Scorecards</div>
        <div class="scorecard-methodology no-print">
          <div><v-icon size="19" color="#14663a">mdi-calculator-variant-outline</v-icon><span><strong>Measured base score</strong>KPI achievement 40% + on-time delivery 20% + attendance 15% + submission 15% + PMR 10%. Missing inputs are reweighted across evidence available for KPI-sheet holders.</span></div>
          <div><v-icon size="19" color="#6a4ca3">mdi-clipboard-pulse-outline</v-icon><span><strong>Executive score</strong>80% measured base + 20% PIP delivered till date when PIP work is due. With no due PIP, the measured base remains unchanged.</span></div>
          <div><v-icon size="19" color="#b56b00">mdi-account-check-outline</v-icon><span><strong>Coverage</strong>Active staff and KPI-sheet staff are shown separately. A missing sheet or unavailable score is never counted as zero performance.</span></div>
        </div>
        <section class="card table-card">
          <table class="an-table scorecard-table">
            <thead><tr><th>Division</th><th class="r">Active staff</th><th class="r">KPI coverage</th><th class="r">Targets</th><th>Measured base</th><th class="r">PIP delivered till date</th><th>Executive score</th><th class="r">Attendance</th><th class="r">Submission</th><th class="r">KPI not met</th><th class="r">Exits</th></tr></thead>
            <tbody>
              <tr v-for="d in divisionScorecards" :key="d.division">
                <td class="strong">{{ d.division }}</td>
                <td class="r">{{ d.activeStaff }}</td>
                <td class="r"><span :class="d.kpiCoverageRate < 50 ? 'warn-txt' : 'good-txt'">{{ d.kpiStaff }}/{{ d.activeStaff }} · {{ d.kpiCoverageRate }}%</span></td>
                <td class="r">{{ d.targets }}</td>
                <td><template v-if="d.baseScore != null"><div class="score-cell"><div class="score-bar"><div class="score-fill" :style="{ width: d.baseScore + '%', background: scoreColor(d.baseScore) }"></div></div><span class="score-num">{{ d.baseScore }}</span></div></template><span v-else class="dim">No KPI base</span></td>
                <td class="r"><span :class="d.pipIncluded ? 'good-txt' : 'dim'">{{ d.pipIncluded ? d.pipDeliveryRate + '%' : 'Not due' }}</span><small v-if="d.pipIncluded" class="manager-pip-meta">{{ d.pipDelivered }}/{{ d.pipDue }} delivered</small></td>
                <td><template v-if="d.executiveScore != null"><div class="score-cell"><div class="score-bar"><div class="score-fill" :style="{ width: d.executiveScore + '%', background: scoreColor(d.executiveScore) }"></div></div><span class="score-num">{{ d.executiveScore }}</span></div></template><span v-else class="dim">Insufficient evidence</span></td>
                <td class="r">{{ d.avgAttendance == null ? '—' : d.avgAttendance + '%' }}</td>
                <td class="r">{{ d.baseScore == null ? '—' : d.submissionRate + '%' }}</td>
                <td class="r"><span :class="d.notMet > 0 ? 'bad' : 'dim'">{{ d.notMet }}</span></td>
                <td class="r"><span :class="d.exits > 0 ? 'bad' : 'dim'">{{ d.exits }}</span></td>
              </tr>
              <tr v-if="!divisionScorecards.length"><td colspan="11" class="dim center">No division scorecards are available.</td></tr>
            </tbody>
          </table>
        </section>

        <!-- Manager Effectiveness -->
        <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-account-tie</v-icon> Manager Effectiveness &amp; Approval Bottlenecks</div>
        <div class="insight no-print"><v-icon size="18" color="#14663a">mdi-information-outline</v-icon><span>Combined manager-team score uses <b>80% base team effectiveness</b> and <b>20% PIP delivered-till-date rate</b> when PIP work is due. Scores remain provisional while KPI adoption is limited.</span></div>
        <section class="card table-card">
          <table class="an-table">
            <thead><tr><th>Manager</th><th class="r">Team</th><th>Base team score</th><th class="r">PIP delivered till date</th><th>Combined team score</th><th class="r">Pending approvals</th><th class="r">Oldest pending</th></tr></thead>
            <tbody>
              <tr v-for="m in a.managers" :key="m.id">
                <td class="strong">{{ m.name || '—' }}</td>
                <td class="r">{{ m.teamSize }}</td>
                <td><div class="score-cell"><div class="score-bar"><div class="score-fill" :style="{ width: m.baseTeamScore + '%', background: scoreColor(m.baseTeamScore) }"></div></div><span class="score-num">{{ m.baseTeamScore }}</span></div></td>
                <td class="r"><span :class="m.pipIncluded ? 'good-txt' : 'dim'">{{ m.pipIncluded ? m.pipDeliveryRate + '%' : 'Not due' }}</span><small v-if="m.pipIncluded" class="manager-pip-meta">{{ m.pipDelivered }}/{{ m.pipDue }} delivered · {{ m.pipCoveredTeamSize }}/{{ m.teamSize }} staff mapped</small></td>
                <td><div class="score-cell"><div class="score-bar"><div class="score-fill" :style="{ width: m.teamAvgScore + '%', background: scoreColor(m.teamAvgScore) }"></div></div><span class="score-num">{{ m.teamAvgScore }}</span></div></td>
                <td class="r"><span :class="m.pendingApprovals > 0 ? 'warn-txt' : 'dim'">{{ m.pendingApprovals }}</span></td>
                <td class="r"><span :class="m.oldestPendingDays > 0 ? 'warn-txt' : 'dim'">{{ m.oldestPendingDays ? m.oldestPendingDays + 'd' : '—' }}</span></td>
              </tr>
              <tr v-if="!a.managers.length"><td colspan="7" class="dim center">No reporting-manager data.</td></tr>
            </tbody>
          </table>
        </section>

        <!-- Employee Evidence Scorecards -->
        <div class="section-eyebrow">
          <v-icon size="16" color="#14663a">mdi-account-details-outline</v-icon> Employee Performance Evidence
          <span class="eye-sub no-print">All active employees · click any row to open their 360° profile</span>
        </div>
        <section class="controls no-print">
          <div class="view-toggle">
            <button :class="['vt', view === 'top' && 'on']" @click="view = 'top'">Top</button>
            <button :class="['vt', view === 'attention' && 'on']" @click="view = 'attention'">Needs attention</button>
            <button :class="['vt', view === 'all' && 'on']" @click="view = 'all'">All</button>
          </div>
          <div class="filters">
            <div class="search-box"><v-icon size="17" color="#8a978f">mdi-magnify</v-icon><input v-model="search" type="text" placeholder="Search name or ID" /></div>
            <select v-model="divisionFilter" class="sel"><option value="">All divisions</option><option v-for="d in divisionNames" :key="d" :value="d">{{ d }}</option></select>
          </div>
        </section>
        <section class="card table-card">
          <table class="an-table employee-evidence-table">
            <thead><tr><th>#</th><th>Name</th><th>Division</th><th>Performance index</th><th class="r">KPI</th><th class="r">PIP</th><th class="r">On-time</th><th class="r">Attendance</th><th class="r">Submission</th><th class="r">PMR</th><th class="r">Evidence</th></tr></thead>
            <tbody>
              <tr v-for="(e, i) in shownEmployees" :key="e.id" class="clickable-row" @click="openEmployee360(e.id)" title="Click to view 360 profile">
                <td class="dim">{{ i + 1 }}</td>
                <td class="strong emp-link">
                  <span>{{ e.name }}</span>
                  <span class="dim">{{ e.employeeId ? '· ' + e.employeeId : '' }}</span>
                  <span v-if="e.onProbation" class="prob-tag">probation</span>
                  <v-icon size="14" color="#14663a" class="hover-icon">mdi-chevron-right</v-icon>
                </td>
                <td><span class="proj-chip">{{ e.division }}</span></td>
                <td><template v-if="e.performanceIndex != null"><div class="score-cell"><div class="score-bar"><div class="score-fill" :style="{ width: e.performanceIndex + '%', background: scoreColor(e.performanceIndex) }"></div></div><span class="score-num">{{ e.performanceIndex }}</span></div></template><span v-else class="dim">Insufficient evidence</span></td>
                <td class="r">{{ talentMetric(e.metPercent) }}</td>
                <td class="r">{{ talentMetric(e.pipDeliveryRate) }}</td>
                <td class="r">{{ talentMetric(e.onTimeRate) }}</td>
                <td class="r">{{ talentMetric(e.attendancePct) }}</td>
                <td class="r">{{ talentMetric(e.submissionRate) }}</td>
                <td class="r">{{ e.pmrRating == null ? '—' : e.pmrRating + '/10' }}</td>
                <td class="r"><span class="evidence-pill">{{ e.evidenceCoverage }}%</span><small v-if="e.reasons && e.reasons.length" class="evidence-gap-label">{{ e.reasons.join(' · ') }}</small></td>
              </tr>
              <tr v-if="!shownEmployees.length"><td colspan="11" class="dim center">No employees match.</td></tr>
            </tbody>
          </table>
        </section>
      </div>

      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <!-- TAB 2: PIP DIVISION COMPARISONS                                    -->
      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <div v-else-if="activeTab === 'pip_comparison'">
        <div class="section-eyebrow">
          <v-icon size="16" color="#14663a">mdi-clipboard-pulse-outline</v-icon> PIP Delivery Comparisons
          <span class="eye-sub">Division evidence through {{ pipScopeLabel }} · future pending assessments excluded from the delivered percentage</span>
        </div>

        <div v-if="!pipReady" class="pip-unavailable card">
          <v-icon size="28" color="#8a978f">mdi-database-alert-outline</v-icon>
          <div><strong>PIP comparison data is unavailable for FY {{ selectedFY || financialYear }}</strong><span>Review sheets may not yet be configured or synced for this financial year.</span></div>
        </div>

        <template v-else>
          <div class="pip-methodology no-print">
            <div><v-icon size="19" color="#14663a">mdi-identifier</v-icon><span><strong>Unique item rule</strong>An Activity, Output or Outcome ID counts once within its project sheet. If the ID repeats across quarters, its latest-quarter row supplies the current status.</span></div>
            <div><v-icon size="19" color="#6a4ca3">mdi-calculator-variant-outline</v-icon><span><strong>Delivered till date</strong>(Met + Delayed Met) ÷ (Met + Delayed Met + Not Met + Overdue) × 100. Future pending and Not Applicable items are excluded.</span></div>
            <div><v-icon size="19" color="#b56b00">mdi-timeline-alert-outline</v-icon><span><strong>Status treatment</strong>Not Met is explicitly recorded in the sheet. Overdue means an unfinished item whose end date has passed. Pending means its deadline is still in the future.</span></div>
          </div>
          <div class="pip-exec-grid">
            <div class="pip-exec-card featured"><span>Delivered till date</span><strong>{{ pipSummary.due ? pipSummary.dueDeliveryRate + '%' : 'Not due' }}</strong><small>{{ num(pipSummary.delivered) }} delivered ÷ {{ num(pipSummary.due) }} due; pending and N/A excluded</small></div>
            <div class="pip-exec-card"><span>Unique review items</span><strong>{{ num(pipSummary.reviewItems) }}</strong><small>Repeated IDs count once; latest-quarter status retained · {{ num(pipSummary.notApplicable) }} N/A</small></div>
            <div class="pip-exec-card risk"><span>Needs attention</span><strong>{{ num(Number(pipSummary.notMet || 0) + Number(pipSummary.overdue || 0)) }}</strong><small>{{ num(pipSummary.notMet) }} not met · {{ num(pipSummary.overdue) }} overdue</small></div>
            <div class="pip-exec-card future"><span>Future work</span><strong>{{ num(pipSummary.pending) }}</strong><small>Pending beyond the reporting date · excluded from delivery</small></div>
            <div class="pip-exec-card"><span>Staff linked</span><strong>{{ num(pipSummary.mappedEmployees) }}</strong><small><template v-if="pipStaffCoverageRate != null">{{ pipStaffCoverageRate }}% of {{ num(activeEmployeeCount) }} active employees · </template>{{ num(pipSummary.divisionSources) }} workbooks</small></div>
            <div class="pip-exec-card" :class="{ risk: pipSummary.unresolvedLabels > 0 }"><span>Mapping gaps</span><strong>{{ num(pipSummary.unresolvedLabels) }}</strong><small>Unresolved point-person labels</small></div>
          </div>

          <section v-if="pipWatchlist.length" class="pip-watch-grid">
            <button v-for="row in pipWatchlist" :key="row.id" class="pip-watch-card" @click="openPipDivision(row)">
              <div class="pip-watch-top"><span>{{ row.name }}</span><strong>{{ row.counts.due ? row.counts.dueDeliveryRate + '%' : 'Not due' }}</strong></div>
              <div class="pip-watch-bar"><span :style="{ width: Math.min(100, row.counts.dueDeliveryRate || 0) + '%' }"></span></div>
              <div class="pip-watch-foot"><span>{{ Number(row.counts.notMet || 0) + Number(row.counts.overdue || 0) }} need attention</span><span>{{ row.employees }} linked staff</span></div>
            </button>
          </section>

          <div class="section-eyebrow compact">
            <v-icon size="16" color="#14663a">mdi-sitemap-outline</v-icon> Division Evidence Table
            <span class="eye-sub">Click a division to open its full PIP analysis</span>
          </div>
          <section class="controls no-print">
            <div class="view-toggle">
              <button :class="['vt', pipSort === 'attention' && 'on']" @click="pipSort = 'attention'">Needs attention</button>
              <button :class="['vt', pipSort === 'delivery' && 'on']" @click="pipSort = 'delivery'">Delivery rate</button>
              <button :class="['vt', pipSort === 'name' && 'on']" @click="pipSort = 'name'">A–Z</button>
            </div>
            <div class="filters pip-table-filters">
              <div class="search-box"><v-icon size="17" color="#8a978f">mdi-magnify</v-icon><input v-model="pipDivisionSearch" type="text" placeholder="Search division" /></div>
              <select v-model="pipStatusFilter" class="sel"><option value="all">All statuses</option><option value="attention">Needs attention</option><option value="due">Has due work</option><option value="not_due">No due work</option></select>
              <button class="btn ghost small" @click="$router.push({ path: '/ceo-pip', query: { year: selectedFY || financialYear, tab: 'divisions' } })"><v-icon size="16">mdi-open-in-new</v-icon> Full PIP intelligence</button>
            </div>
          </section>
          <section class="card table-card">
            <table class="an-table pip-compare-table">
              <thead><tr><th>Division</th><th class="r">PIP-linked staff</th><th class="r">Unique items</th><th class="r">Met</th><th class="r">Delayed met</th><th class="r">Not met</th><th class="r">Overdue</th><th class="r">Future</th><th>Delivered till date</th><th>Measured base</th></tr></thead>
              <tbody>
                <tr v-for="row in sortedPipDivisions" :key="row.id" class="clickable-row" @click="openPipDivision(row)">
                  <td class="strong pip-division-name"><span>{{ row.name }}</span><small>{{ row.code || 'No shortcode' }}</small><v-icon size="14" color="#14663a">mdi-chevron-right</v-icon></td>
                  <td class="r"><span>{{ row.employees }}</span><small v-if="row.activeStaff != null" class="manager-pip-meta">{{ row.activeStaff }} active in division roster</small></td>
                  <td class="r">{{ row.counts.reviewItems || 0 }}</td>
                  <td class="r good-txt">{{ row.counts.met || 0 }}</td>
                  <td class="r warn-txt">{{ row.counts.delayedMet || 0 }}</td>
                  <td class="r bad">{{ row.counts.notMet || 0 }}</td>
                  <td class="r overdue-txt">{{ row.counts.overdue || 0 }}</td>
                  <td class="r dim">{{ row.counts.pending || 0 }}</td>
                  <td><div class="score-cell"><div class="score-bar"><div class="score-fill" :style="{ width: Math.min(100, row.counts.dueDeliveryRate || 0) + '%', background: deliveryColor(row.counts.dueDeliveryRate) }"></div></div><span class="score-num">{{ row.counts.due ? row.counts.dueDeliveryRate + '%' : '—' }}</span></div><small v-if="row.counts.due" class="delivery-formula">{{ row.counts.delivered }}/{{ row.counts.due }} delivered</small><small v-else class="delivery-formula">No work due yet</small></td>
                  <td><template v-if="row.kpi"><div class="score-cell"><div class="score-bar"><div class="score-fill" :style="{ width: row.kpi.score + '%', background: scoreColor(row.kpi.score) }"></div></div><span class="score-num">{{ row.kpi.score }}</span></div></template><span v-else class="dim">No KPI scorecard</span></td>
                </tr>
                <tr v-if="!sortedPipDivisions.length"><td colspan="10" class="dim center">No divisions match the current filters.</td></tr>
              </tbody>
            </table>
          </section>

          <div class="data-health"><v-icon size="16" :color="pipDataStatus.failedSources ? '#c62828' : '#2e7d32'">{{ pipDataStatus.failedSources ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline' }}</v-icon><span>{{ pipDataStatus.checkedSources || 0 }} of {{ pipDataStatus.sources || 0 }} PIP workbooks checked<template v-if="pipDataStatus.lastCheckedAt"> · last checked {{ formatDateTime(pipDataStatus.lastCheckedAt) }}</template><template v-if="pipDataStatus.lastChangedAt"> · source data last changed {{ formatDateTime(pipDataStatus.lastChangedAt) }}</template><template v-if="pipDataStatus.failedSources"> · {{ pipDataStatus.failedSources }} failed</template></span></div>
        </template>
      </div>

      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <!-- TAB 3: 9-BOX TALENT & PERFORMANCE MATRIX                            -->
      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <div v-else-if="activeTab === 'talent_matrix'">
        <div class="section-eyebrow">
          <v-icon size="16" color="#14663a">mdi-grid</v-icon> Talent &amp; Performance 9-Box Matrix
          <span class="eye-sub">PMR potential vs. a performance index built from KPI, PIP, timeliness, attendance and target engagement</span>
        </div>

        <div class="talent-methodology">
          <div><v-icon size="19" color="#14663a">mdi-calculator-variant-outline</v-icon><span><strong>Performance index</strong>KPI 35% + PIP 25% + on-time delivery 15% + attendance 15% + target submission 10%. Missing signals are reweighted across available evidence.</span></div>
          <div><v-icon size="19" color="#6a4ca3">mdi-account-star-outline</v-icon><span><strong>Potential</strong>PMR appraisal only. PMR is kept out of the performance score so the same appraisal is not counted twice.</span></div>
          <div><v-icon size="19" color="#b56b00">mdi-shield-check-outline</v-icon><span><strong>Classification rule</strong>At least one KPI/PIP delivery signal, one additional signal and a PMR rating are required. Others remain visible under data gaps.</span></div>
        </div>

        <div class="matrix-overview-grid">
          <div class="mo-card population">
            <div class="mo-val">{{ talentData ? talentData.eligibleEmployees : 0 }}</div>
            <div class="mo-title">Active employees reviewed</div>
            <div class="mo-sub">Current staff in the selected financial year</div>
          </div>
          <div class="mo-card star">
            <div class="mo-val">{{ talentData ? talentData.totalEmployees : 0 }}</div>
            <div class="mo-title">Placed in the 9-box</div>
            <div class="mo-sub">{{ talentData ? talentData.coverageRate : 0 }}% evidence coverage</div>
          </div>
          <div class="mo-card core">
            <div class="mo-val">{{ getQuadrantCount(['high_high', 'high_med']) }}</div>
            <div class="mo-title">High-impact employees</div>
            <div class="mo-sub">High performance with medium/high potential</div>
          </div>
          <div class="mo-card risk">
            <div class="mo-val">{{ getQuadrantCount(['low_low', 'low_med', 'low_high']) }}</div>
            <div class="mo-title">Delivery attention</div>
            <div class="mo-sub">Low composite performance across potential levels</div>
          </div>
          <div class="mo-card unrated">
            <div class="mo-val">{{ talentData ? talentData.insufficientEvidenceCount : 0 }}</div>
            <div class="mo-title">Evidence gaps</div>
            <div class="mo-sub">{{ talentData ? talentData.unratedCount : 0 }} without PMR · excluded from classification</div>
          </div>
        </div>

        <!-- Matrix Filters -->
        <div class="controls no-print" style="margin-top:16px;">
          <div class="filters">
            <div class="search-box"><v-icon size="17" color="#8a978f">mdi-magnify</v-icon><input v-model="talentSearch" type="text" placeholder="Search staff name..." /></div>
            <select v-model="talentDivFilter" class="sel">
              <option value="">All divisions</option>
              <option v-for="d in talentDivisionNames" :key="d" :value="d">{{ d }}</option>
            </select>
          </div>
          <div class="matrix-legend">
            <span class="leg-item"><span class="leg-dot red"></span> Low &lt; 40</span>
            <span class="leg-item"><span class="leg-dot blue"></span> Medium 40–69.9</span>
            <span class="leg-item"><span class="leg-dot green"></span> High ≥ 70</span>
          </div>
        </div>

        <!-- 3x3 Matrix Grid -->
        <div class="matrix-container card">
          <div class="matrix-y-axis-label">▲ Potential (Appraisal / PMR Rating)</div>
          <div class="matrix-wrapper">
            <div class="matrix-grid">
              <!-- Row 1: High Potential -->
              <div class="matrix-cell row-high col-low" :class="{ selected: selectedQuadrantKey === 'low_high' }" @click="selectQuadrant('low_high')">
                <div class="mc-head"><span class="mc-tag orange">Enigmas</span><span class="mc-count">{{ filteredQuadrantEmps('low_high').length }}</span></div>
                <div class="mc-desc">High potential · Low composite performance</div><div class="mc-people"><span v-for="employee in filteredQuadrantEmps('low_high').slice(0, 3)" :key="employee.id">{{ employee.name }}</span><small v-if="filteredQuadrantEmps('low_high').length > 3">+{{ filteredQuadrantEmps('low_high').length - 3 }} more</small></div>
              </div>
              <div class="matrix-cell row-high col-med" :class="{ selected: selectedQuadrantKey === 'med_high' }" @click="selectQuadrant('med_high')">
                <div class="mc-head"><span class="mc-tag blue">High Potential</span><span class="mc-count">{{ filteredQuadrantEmps('med_high').length }}</span></div>
                <div class="mc-desc">High potential · Medium composite performance</div><div class="mc-people"><span v-for="employee in filteredQuadrantEmps('med_high').slice(0, 3)" :key="employee.id">{{ employee.name }}</span><small v-if="filteredQuadrantEmps('med_high').length > 3">+{{ filteredQuadrantEmps('med_high').length - 3 }} more</small></div>
              </div>
              <div class="matrix-cell row-high col-high" :class="{ selected: selectedQuadrantKey === 'high_high' }" @click="selectQuadrant('high_high')">
                <div class="mc-head"><span class="mc-tag green">Star Talent</span><span class="mc-count">{{ filteredQuadrantEmps('high_high').length }}</span></div>
                <div class="mc-desc">High potential · High composite performance</div><div class="mc-people"><span v-for="employee in filteredQuadrantEmps('high_high').slice(0, 3)" :key="employee.id">{{ employee.name }}</span><small v-if="filteredQuadrantEmps('high_high').length > 3">+{{ filteredQuadrantEmps('high_high').length - 3 }} more</small></div>
              </div>

              <!-- Row 2: Medium Potential -->
              <div class="matrix-cell row-med col-low" :class="{ selected: selectedQuadrantKey === 'low_med' }" @click="selectQuadrant('low_med')">
                <div class="mc-head"><span class="mc-tag orange">Needs Coaching</span><span class="mc-count">{{ filteredQuadrantEmps('low_med').length }}</span></div>
                <div class="mc-desc">Medium potential · Low composite performance</div><div class="mc-people"><span v-for="employee in filteredQuadrantEmps('low_med').slice(0, 3)" :key="employee.id">{{ employee.name }}</span><small v-if="filteredQuadrantEmps('low_med').length > 3">+{{ filteredQuadrantEmps('low_med').length - 3 }} more</small></div>
              </div>
              <div class="matrix-cell row-med col-med" :class="{ selected: selectedQuadrantKey === 'med_med' }" @click="selectQuadrant('med_med')">
                <div class="mc-head"><span class="mc-tag blue">Core Professionals</span><span class="mc-count">{{ filteredQuadrantEmps('med_med').length }}</span></div>
                <div class="mc-desc">Medium potential · Medium composite performance</div><div class="mc-people"><span v-for="employee in filteredQuadrantEmps('med_med').slice(0, 3)" :key="employee.id">{{ employee.name }}</span><small v-if="filteredQuadrantEmps('med_med').length > 3">+{{ filteredQuadrantEmps('med_med').length - 3 }} more</small></div>
              </div>
              <div class="matrix-cell row-med col-high" :class="{ selected: selectedQuadrantKey === 'high_med' }" @click="selectQuadrant('high_med')">
                <div class="mc-head"><span class="mc-tag green">High Performers</span><span class="mc-count">{{ filteredQuadrantEmps('high_med').length }}</span></div>
                <div class="mc-desc">Medium potential · High composite performance</div><div class="mc-people"><span v-for="employee in filteredQuadrantEmps('high_med').slice(0, 3)" :key="employee.id">{{ employee.name }}</span><small v-if="filteredQuadrantEmps('high_med').length > 3">+{{ filteredQuadrantEmps('high_med').length - 3 }} more</small></div>
              </div>

              <!-- Row 3: Low Potential -->
              <div class="matrix-cell row-low col-low" :class="{ selected: selectedQuadrantKey === 'low_low' }" @click="selectQuadrant('low_low')">
                <div class="mc-head"><span class="mc-tag red">At-Risk</span><span class="mc-count">{{ filteredQuadrantEmps('low_low').length }}</span></div>
                <div class="mc-desc">Low potential · Low composite performance</div><div class="mc-people"><span v-for="employee in filteredQuadrantEmps('low_low').slice(0, 3)" :key="employee.id">{{ employee.name }}</span><small v-if="filteredQuadrantEmps('low_low').length > 3">+{{ filteredQuadrantEmps('low_low').length - 3 }} more</small></div>
              </div>
              <div class="matrix-cell row-low col-med" :class="{ selected: selectedQuadrantKey === 'med_low' }" @click="selectQuadrant('med_low')">
                <div class="mc-head"><span class="mc-tag orange">Steady Workers</span><span class="mc-count">{{ filteredQuadrantEmps('med_low').length }}</span></div>
                <div class="mc-desc">Low potential · Medium composite performance</div><div class="mc-people"><span v-for="employee in filteredQuadrantEmps('med_low').slice(0, 3)" :key="employee.id">{{ employee.name }}</span><small v-if="filteredQuadrantEmps('med_low').length > 3">+{{ filteredQuadrantEmps('med_low').length - 3 }} more</small></div>
              </div>
              <div class="matrix-cell row-low col-high" :class="{ selected: selectedQuadrantKey === 'high_low' }" @click="selectQuadrant('high_low')">
                <div class="mc-head"><span class="mc-tag blue">Execution Specialists</span><span class="mc-count">{{ filteredQuadrantEmps('high_low').length }}</span></div>
                <div class="mc-desc">Low potential · High composite performance</div><div class="mc-people"><span v-for="employee in filteredQuadrantEmps('high_low').slice(0, 3)" :key="employee.id">{{ employee.name }}</span><small v-if="filteredQuadrantEmps('high_low').length > 3">+{{ filteredQuadrantEmps('high_low').length - 3 }} more</small></div>
              </div>
            </div>
            <div class="matrix-x-axis-label">Performance index (KPI + PIP + timeliness + attendance + engagement) ►</div>
          </div>
        </div>

        <!-- Selected Quadrant Staff Drilldown -->
        <div v-if="selectedQuadrant" class="quadrant-detail-box card" style="margin-top:16px;">
          <div class="qd-head">
            <div>
              <div class="qd-title"><v-icon size="20" :color="selectedQuadrant.color">mdi-account-group</v-icon> {{ selectedQuadrant.title }}</div>
              <div class="qd-sub">{{ selectedQuadrant.desc }} · {{ selectedQuadrantEmps.length }} Staff (click any row to view 360° profile)</div>
            </div>
            <button class="btn ghost small no-print" @click="selectedQuadrantKey = ''"><v-icon size="16">mdi-close</v-icon> Clear Selection</button>
          </div>
          <div class="talent-table-wrap" v-if="selectedQuadrantEmps.length"><table class="an-table">
            <thead><tr><th>#</th><th>Name</th><th>Division</th><th>Manager</th><th class="r">Performance</th><th class="r">KPI</th><th class="r">PIP</th><th class="r">On-time</th><th class="r">Attendance</th><th class="r">PMR</th><th class="r">Evidence</th></tr></thead>
            <tbody>
              <tr v-for="(e, i) in selectedQuadrantEmps" :key="e.id" class="clickable-row" @click="openEmployee360(e.id)" title="Click to view 360 profile">
                <td class="dim">{{ i + 1 }}</td>
                <td class="strong emp-link">
                  <span>{{ e.name }}</span>
                  <span class="dim">{{ e.employeeId ? '· ' + e.employeeId : '' }}</span>
                  <v-icon size="14" color="#14663a" class="hover-icon">mdi-chevron-right</v-icon>
                </td>
                <td><span class="proj-chip">{{ e.division }}</span></td>
                <td>{{ e.manager || '—' }}</td>
                <td class="r"><strong class="performance-score">{{ e.performanceIndex }}%</strong></td>
                <td class="r">{{ talentMetric(e.metPercent) }}</td>
                <td class="r">{{ talentMetric(e.pipDeliveryRate) }}</td>
                <td class="r">{{ talentMetric(e.onTimeRate) }}</td>
                <td class="r">{{ talentMetric(e.attendancePct) }}</td>
                <td class="r">{{ e.pmrRating != null ? e.pmrRating + '/10' : '—' }}</td>
                <td class="r"><span class="evidence-pill">{{ e.evidenceCoverage }}%</span></td>
              </tr>
            </tbody>
          </table></div>
          <div v-else class="dim center" style="padding:20px;">No staff in this quadrant match your search/filters.</div>
        </div>

        <section v-if="talentData && talentData.insufficientEvidenceCount" class="card talent-gaps-card">
          <div class="qd-head"><div><div class="qd-title"><v-icon size="20" color="#b56b00">mdi-database-alert-outline</v-icon> Employees awaiting classification</div><div class="qd-sub">Kept outside the matrix so missing evidence is never interpreted as low performance or low potential.</div></div><span class="gap-count">{{ filteredTalentGaps.length }} shown</span></div>
          <div class="talent-table-wrap"><table class="an-table"><thead><tr><th>Name</th><th>Division</th><th>Reason</th><th class="r">KPI targets</th><th class="r">PIP items</th><th class="r">PMR</th><th class="r">Evidence available</th></tr></thead><tbody><tr v-for="employee in filteredTalentGaps" :key="employee.id" class="clickable-row" @click="openEmployee360(employee.id)"><td class="strong">{{ employee.name }} <small>{{ employee.employeeId || '' }}</small></td><td>{{ employee.division }}</td><td><span class="gap-reason" v-for="reason in employee.reasons" :key="reason">{{ reason }}</span></td><td class="r">{{ employee.targets || 0 }}</td><td class="r">{{ employee.pipCounts ? employee.pipCounts.reviewItems : 0 }}</td><td class="r">{{ employee.pmrRating != null ? employee.pmrRating + '/10' : '—' }}</td><td class="r">{{ employee.evidenceCoverage }}%</td></tr><tr v-if="!filteredTalentGaps.length"><td colspan="7" class="dim center">No evidence gaps match the current filters.</td></tr></tbody></table></div>
        </section>
      </div>

      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <!-- TAB 4: PROJECT PERFORMANCE BENCHMARK                                -->
      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <div v-else-if="activeTab === 'projects'">
        <div class="section-eyebrow">
          <v-icon size="16" color="#14663a">mdi-compass-outline</v-icon> Project Delivery &amp; Resource Benchmark
          <span class="eye-sub">Governance, mapped-team KPI indicators and linked PIP delivery</span>
        </div>

        <div class="project-methodology no-print">
          <div><v-icon size="19" color="#14663a">mdi-account-group-outline</v-icon><span><strong>Mapped-team evidence</strong>KPI sheets belong to employees, not projects. The team score averages KPI 40%, timeliness 20%, attendance 15%, submission 15% and PMR 10% across mapped staff, reweighted when evidence is missing.</span></div>
          <div><v-icon size="19" color="#6a4ca3">mdi-clipboard-pulse-outline</v-icon><span><strong>Project PIP evidence</strong>Only PIP tabs linked to the existing project are used. Repeated IDs count once and the latest-quarter status supplies the current outcome.</span></div>
          <div><v-icon size="19" color="#b56b00">mdi-scale-balance</v-icon><span><strong>No artificial composite</strong>KPI proxy and PIP delivery remain separate because they measure different evidence. Missing linkage or coverage is shown as a governance gap, not a zero score.</span></div>
        </div>

        <div class="proj-kpi-grid">
          <div class="pk-card">
            <div class="pk-val">{{ projectPortfolio.total }}</div>
            <div class="pk-lbl">Active Projects</div>
          </div>
          <div class="pk-card" :class="{ warn: projectPortfolio.missingPi > 0 }">
            <div class="pk-val">{{ projectPortfolio.withPi }}/{{ projectPortfolio.total }}</div>
            <div class="pk-lbl">Projects with PI</div>
          </div>
          <div class="pk-card" :class="{ warn: projectPortfolio.unstaffed > 0 }">
            <div class="pk-val">{{ projectPortfolio.staffed }}/{{ projectPortfolio.total }}</div>
            <div class="pk-lbl">Projects with Staff</div>
          </div>
          <div class="pk-card">
            <div class="pk-val">{{ projectPortfolio.kpiEvidence }}/{{ projectPortfolio.total }}</div>
            <div class="pk-lbl">With Team KPI Evidence</div>
          </div>
          <div class="pk-card">
            <div class="pk-val">{{ projectPortfolio.pipLinked }}/{{ projectPortfolio.total }}</div>
            <div class="pk-lbl">Linked to PIP Tabs</div>
          </div>
          <div class="pk-card warn">
            <div class="pk-val">{{ projectPortfolio.attention }}</div>
            <div class="pk-lbl">Projects Needing Attention</div>
          </div>
        </div>

        <!-- Project Controls -->
        <section class="controls no-print" style="margin-top:16px;">
          <div class="view-toggle">
            <button :class="['vt', projectFilterStatus === 'all' && 'on']" @click="projectFilterStatus = 'all'">All Projects</button>
            <button :class="['vt', projectFilterStatus === 'attention' && 'on']" @click="projectFilterStatus = 'attention'">Needs Attention</button>
            <button :class="['vt', projectFilterStatus === 'pip' && 'on']" @click="projectFilterStatus = 'pip'">PIP Linked</button>
            <button :class="['vt', projectFilterStatus === 'no_evidence' && 'on']" @click="projectFilterStatus = 'no_evidence'">No Delivery Evidence</button>
          </div>
          <div class="filters">
            <div class="search-box"><v-icon size="17" color="#8a978f">mdi-magnify</v-icon><input v-model="projectSearch" type="text" placeholder="Search project name..." /></div>
            <select v-model="projectDivFilter" class="sel">
              <option value="">All divisions</option>
              <option v-for="d in projectDivisions" :key="d" :value="d">{{ d }}</option>
            </select>
          </div>
        </section>

        <!-- Project Table -->
        <section class="card table-card">
          <table class="an-table project-benchmark-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Project</th>
                <th>Division / Location</th>
                <th>PI</th>
                <th>Co-PIs</th>
                <th class="r">Team</th>
                <th class="r">KPI Coverage</th>
                <th>Team KPI Closure</th>
                <th>Team Evidence Score</th>
                <th class="r">PIP Items</th>
                <th>PIP Delivered Till Date</th>
                <th class="r">PIP Attention</th>
                <th>Evidence / Governance</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, idx) in filteredProjects" :key="p.id" class="clickable-row" @click="openProjectBenchmark(p)" title="Open linked project in PIP intelligence">
                <td class="dim">{{ idx + 1 }}</td>
                <td class="strong">
                  {{ p.projectName }}
                  <small class="project-code">{{ p.code || 'No shortcode' }}</small>
                  <span v-if="p.isFundRaise" class="fr-tag" title="Fundraising Enabled">Fundraise</span>
                </td>
                <td><span :class="p.hasDivision ? 'proj-chip' : 'gap-reason'">{{ p.division }}</span><small class="project-location">{{ p.location }}</small></td>
                <td><span :class="p.primaryPi ? 'strong' : 'bad'">{{ p.primaryPi ? p.primaryPi.name : 'Not assigned' }}</span><small v-if="p.primaryPi && p.primaryPi.type === 'external'" class="external-pi">Advisor / consultant</small></td>
                <td><span v-if="p.coPis && p.coPis.length" class="co-pi-list"><small v-for="person in p.coPis" :key="person.type + ':' + person.id">{{ person.name }}</small></span><span v-else class="dim">—</span></td>
                <td class="r strong">{{ p.teamSize }}</td>
                <td class="r"><span :class="p.teamSize && p.kpiCoverageRate < 100 ? 'warn-txt' : (p.teamSize ? 'good-txt' : 'dim')">{{ p.teamSize ? p.kpiCoveredTeamSize + '/' + p.teamSize + ' · ' + p.kpiCoverageRate + '%' : 'No staff' }}</span></td>
                <td>
                  <div v-if="p.totalTargets" class="score-cell">
                    <div class="score-bar"><div class="score-fill" :style="{ width: p.completionRate + '%', background: scoreColor(p.completionRate) }"></div></div>
                    <span class="score-num">{{ p.completionRate }}%</span>
                  </div>
                  <span v-else class="dim">No KPI items</span>
                  <small v-if="p.totalTargets" class="delivery-formula">{{ p.totalMet + p.totalDelayed }}/{{ p.totalTargets }} closed · {{ p.onTimeRate }}% on time</small>
                </td>
                <td><template v-if="p.kpiCoveredTeamSize"><div class="score-cell"><div class="score-bar"><div class="score-fill" :style="{ width: p.projectScore + '%', background: scoreColor(p.projectScore) }"></div></div><span class="score-num">{{ p.projectScore }}</span></div><small class="delivery-formula">Mapped team average</small></template><span v-else class="dim">No KPI evidence</span></td>
                <td class="r">{{ p.pipLinked ? p.pipCounts.reviewItems : '—' }}</td>
                <td><template v-if="p.pipLinked"><div v-if="p.pipCounts.due" class="score-cell"><div class="score-bar"><div class="score-fill" :style="{ width: p.pipCounts.dueDeliveryRate + '%', background: deliveryColor(p.pipCounts.dueDeliveryRate) }"></div></div><span class="score-num">{{ p.pipCounts.dueDeliveryRate }}%</span></div><span v-else class="dim">Not due</span><small class="delivery-formula">{{ p.pipCounts.delivered }}/{{ p.pipCounts.due }} delivered · {{ p.pipCounts.pending }} future</small></template><span v-else class="gap-reason">PIP not linked</span></td>
                <td class="r"><span :class="Number(p.pipCounts.notMet || 0) + Number(p.pipCounts.overdue || 0) ? 'bad' : 'dim'">{{ p.pipLinked ? Number(p.pipCounts.notMet || 0) + Number(p.pipCounts.overdue || 0) : '—' }}</span><small v-if="p.pipLinked" class="manager-pip-meta">{{ p.pipCounts.notMet || 0 }} not met · {{ p.pipCounts.overdue || 0 }} overdue</small></td>
                <td><span v-if="!p.governanceIssues.length" class="status-pill top">Complete setup</span><span v-for="issue in p.governanceIssues" :key="issue" class="project-gap-pill">{{ issue }}</span></td>
              </tr>
              <tr v-if="!filteredProjects.length"><td colspan="13" class="dim center">No projects match the criteria.</td></tr>
            </tbody>
          </table>
        </section>
      </div>

      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <!-- TAB 5: MULTI-YEAR YOY DONATION COMPARISON                           -->
      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <div v-else-if="activeTab === 'donations_yoy'">
        <div class="section-eyebrow">
          <v-icon size="16" color="#14663a">mdi-chart-timeline-variant</v-icon> WTI Portal Donation YoY Comparison
          <span class="eye-sub">Successful online donations · INR and USD reported separately</span>
        </div>

        <div class="yoy-methodology" v-if="yoyData && yoyData.methodology">
          <div><v-icon size="17" color="#14663a">mdi-check-decagram-outline</v-icon><span><strong>Included records</strong>{{ yoyData.methodology.scope }}. {{ yoyData.methodology.dateField }} determines the financial year.</span></div>
          <div><v-icon size="17" color="#14663a">mdi-calendar-range</v-icon><span><strong>Comparison period</strong>{{ yoyData.methodology.yearBoundary }}. Current growth compares the same elapsed dates in both years.</span></div>
          <div><v-icon size="17" color="#14663a">mdi-currency-usd-off</v-icon><span><strong>Currency rule</strong>{{ yoyData.methodology.currencyRule }}.</span></div>
        </div>

        <div v-if="yoyHasQualityWarnings" class="banner warn yoy-quality">
          <v-icon size="18" color="#9a6500">mdi-alert-outline</v-icon>
          {{ num(yoyData.dataQuality.unsupportedCurrencyRecords) }} unsupported-currency, {{ num(yoyData.dataQuality.invalidAmountRecords) }} invalid-amount and {{ num(yoyData.dataQuality.futureDatedSelectedFyRecords) }} future-dated records were excluded from reported values. Counts remain visible for audit.
        </div>

        <div class="yoy-summary-grid" v-if="yoyData">
          <div class="yoy-card" v-for="fy in yoyData.comparison" :key="fy.financialYear">
            <div class="yoy-head">
              <span class="yoy-fy">FY {{ fy.financialYear }}</span>
              <span class="yoy-tx">{{ num(fy.totalSuccess) }} successful</span>
            </div>
            <div class="yoy-val">{{ formatMoney(fy.totalInr) }}</div>
            <div class="yoy-sub">{{ num(fy.countInr) }} INR donations · Avg <b>{{ formatMoney(fy.avgInrTicket) }}</b></div>
            <div class="yoy-sub">Foreign: <b>{{ formatUsd(fy.totalUsd) }}</b> · {{ num(fy.countUsd) }} donations · Avg {{ formatUsd(fy.avgUsdTicket) }}</div>
            <div class="yoy-foot">{{ fy.isComplete ? 'Full financial year' : 'FY to date' }} · {{ formatDate(fy.periodStart) }} – {{ formatDate(fy.periodEnd) }}</div>
          </div>
          <div class="yoy-growth-card" :class="yoyData.yoyGrowthInr == null ? 'neutral' : (yoyData.yoyGrowthInr >= 0 ? 'pos' : 'neg')">
            <div class="yg-lbl">INR YoY growth · matched period</div>
            <div class="yg-val">
              <v-icon :color="yoyData.yoyGrowthInr == null ? '#77877e' : (yoyData.yoyGrowthInr >= 0 ? '#2e7d32' : '#c62828')">
                {{ yoyData.yoyGrowthInr == null ? 'mdi-minus' : (yoyData.yoyGrowthInr >= 0 ? 'mdi-trending-up' : 'mdi-trending-down') }}
              </v-icon>
              {{ yoyData.yoyGrowthInr != null ? (yoyData.yoyGrowthInr > 0 ? '+' : '') + yoyData.yoyGrowthInr + '%' : '—' }}
            </div>
            <div class="yg-sub" v-if="yoyData.growthComparison">{{ formatMoney(yoyData.growthComparison.currentInr) }} vs {{ formatMoney(yoyData.growthComparison.previousInr) }} through the same FY date</div>
            <div class="yoy-formula" v-if="yoyData.growthComparison && yoyData.growthComparison.previousInr">({{ formatMoney(yoyData.growthComparison.currentInr) }} − {{ formatMoney(yoyData.growthComparison.previousInr) }}) ÷ {{ formatMoney(yoyData.growthComparison.previousInr) }} × 100</div>
            <div class="yg-sub">Through {{ yoyData.growthComparison ? formatDate(yoyData.growthComparison.throughDate) : '—' }} · Previous full-FY YoY: {{ yoyData.prevYoyGrowthInr != null ? yoyData.prevYoyGrowthInr + '%' : '—' }}</div>
          </div>
        </div>

        <div class="yoy-comparison-grid" v-if="yoyData && yoyData.growthComparison">
          <div><span>Selected FY · matched INR</span><strong>{{ formatMoney(yoyData.growthComparison.currentInr) }}</strong><small>Through {{ formatDate(yoyData.growthComparison.throughDate) }}</small></div>
          <div><span>Previous FY · matched INR</span><strong>{{ formatMoney(yoyData.growthComparison.previousInr) }}</strong><small>Through {{ formatDate(yoyData.growthComparison.previousThroughDate) }}</small></div>
          <div><span>Absolute change</span><strong :class="yoyData.growthComparison.changeInr >= 0 ? 'positive' : 'negative'">{{ yoyData.growthComparison.changeInr >= 0 ? '+' : '−' }}{{ formatMoney(Math.abs(yoyData.growthComparison.changeInr)) }}</strong><small>INR only; no FX conversion</small></div>
          <div><span>Donation volume change</span><strong>{{ yoyData.yoyCountGrowth != null ? (yoyData.yoyCountGrowth > 0 ? '+' : '') + yoyData.yoyCountGrowth + '%' : '—' }}</strong><small>{{ num(yoyData.growthComparison.currentSuccess) }} vs {{ num(yoyData.growthComparison.previousSuccess) }} successful</small></div>
          <div><span>Average INR ticket change</span><strong>{{ yoyData.yoyAvgTicketGrowth != null ? (yoyData.yoyAvgTicketGrowth > 0 ? '+' : '') + yoyData.yoyAvgTicketGrowth + '%' : '—' }}</strong><small>{{ formatMoney(yoyData.growthComparison.currentAvgInrTicket) }} vs {{ formatMoney(yoyData.growthComparison.previousAvgInrTicket) }}</small></div>
        </div>

        <!-- 12-Month Side-by-Side Seasonality Comparison -->
        <div class="card yoy-chart-card" style="margin-top:16px;" v-if="yoyData && yoyData.comparison.length">
          <div class="chart-head">
            <div>
              <div class="chart-title"><v-icon size="18" color="#14663a">mdi-chart-bar</v-icon> 12-Month {{ yoyCurrency }} Inflow Trajectory (April – March)</div>
              <div class="chart-sub">Monthly portal donations; future months in the selected FY have no recorded value</div>
            </div>
            <div class="yoy-chart-actions">
              <div class="yoy-currency-toggle"><button :class="{ active: yoyCurrency === 'INR' }" @click="yoyCurrency = 'INR'">INR</button><button :class="{ active: yoyCurrency === 'USD' }" @click="yoyCurrency = 'USD'">USD</button></div>
              <div class="chart-legends">
                <span class="cleg cur"><span class="cleg-box c1"></span> FY {{ yoyData.comparison[0].financialYear }} (Selected)</span>
                <span class="cleg p1"><span class="cleg-box c2"></span> FY {{ yoyData.comparison[1].financialYear }}</span>
                <span class="cleg p2"><span class="cleg-box c3"></span> FY {{ yoyData.comparison[2].financialYear }}</span>
              </div>
            </div>
          </div>

          <div class="chart-bars-wrap">
            <div class="month-col" v-for="(m, idx) in yoyData.monthLabels" :key="m">
              <div class="bars-group">
                <div class="bar bar-c1" :style="{ height: getBarHeight(getYoyMonthValue(yoyData.comparison[0], idx)) + '%' }" :title="'FY ' + yoyData.comparison[0].financialYear + ' ' + m + ': ' + formatYoyValue(getYoyMonthValue(yoyData.comparison[0], idx))"></div>
                <div class="bar bar-c2" :style="{ height: getBarHeight(getYoyMonthValue(yoyData.comparison[1], idx)) + '%' }" :title="'FY ' + yoyData.comparison[1].financialYear + ' ' + m + ': ' + formatYoyValue(getYoyMonthValue(yoyData.comparison[1], idx))"></div>
                <div class="bar bar-c3" :style="{ height: getBarHeight(getYoyMonthValue(yoyData.comparison[2], idx)) + '%' }" :title="'FY ' + yoyData.comparison[2].financialYear + ' ' + m + ': ' + formatYoyValue(getYoyMonthValue(yoyData.comparison[2], idx))"></div>
              </div>
              <div class="month-label">{{ m }}</div>
            </div>
          </div>
        </div>

        <div class="card yoy-audit-card" v-if="yoyData && yoyData.comparison.length">
          <div class="chart-head"><div><div class="chart-title"><v-icon size="18" color="#14663a">mdi-table-check</v-icon> Financial-year audit table</div><div class="chart-sub">Amounts and counts behind the cards; currencies remain separate</div></div></div>
          <div class="table-scroll"><table class="data-table yoy-audit-table"><thead><tr><th>Financial year</th><th>Period represented</th><th class="r">INR donations</th><th class="r">INR amount</th><th class="r">Avg INR ticket</th><th class="r">USD donations</th><th class="r">USD amount</th><th class="r">Avg USD ticket</th></tr></thead><tbody><tr v-for="fy in yoyData.comparison" :key="'audit-' + fy.financialYear"><td><strong>FY {{ fy.financialYear }}</strong></td><td>{{ formatDate(fy.periodStart) }} – {{ formatDate(fy.periodEnd) }}<small class="audit-period">{{ fy.isComplete ? 'Full FY' : 'FY to date' }}</small></td><td class="r">{{ num(fy.countInr) }}</td><td class="r strong">{{ formatMoney(fy.totalInr) }}</td><td class="r">{{ formatMoney(fy.avgInrTicket) }}</td><td class="r">{{ num(fy.countUsd) }}</td><td class="r strong">{{ formatUsd(fy.totalUsd) }}</td><td class="r">{{ formatUsd(fy.avgUsdTicket) }}</td></tr></tbody></table></div>
        </div>

        <!-- Top Donation Categories / Appeals Across Years -->
        <div class="grid grid-2" style="margin-top:16px;" v-if="yoyData && yoyData.comparison.length">
          <div class="panel">
            <div class="panel-title mb"><v-icon size="18" color="#14663a">mdi-tag-multiple-outline</v-icon> Current FY Top Appeals</div>
            <ul class="cat-list">
              <li v-for="c in yoyData.comparison[0].categoriesList.slice(0, 6)" :key="c.name">
                <span class="cat-name">{{ c.name }}</span>
                <span class="cat-amt"><b>{{ formatMoney(c.inr) }}</b><small v-if="c.usd"> + {{ formatUsd(c.usd) }}</small><small>{{ num(c.count) }} donations</small></span>
              </li>
              <li v-if="!yoyData.comparison[0].categoriesList.length" class="dim">No categorized donations recorded yet.</li>
            </ul>
          </div>
          <div class="panel">
            <div class="panel-title mb"><v-icon size="18" color="#14663a">mdi-history</v-icon> Previous FY Top Appeals</div>
            <ul class="cat-list">
              <li v-for="c in yoyData.comparison[1].categoriesList.slice(0, 6)" :key="c.name">
                <span class="cat-name">{{ c.name }}</span>
                <span class="cat-amt"><b>{{ formatMoney(c.inr) }}</b><small v-if="c.usd"> + {{ formatUsd(c.usd) }}</small><small>{{ num(c.count) }} donations</small></span>
              </li>
              <li v-if="!yoyData.comparison[1].categoriesList.length" class="dim">No categorized donations recorded.</li>
            </ul>
          </div>
        </div>
      </div>
    </main>

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- 360° EMPLOYEE EXECUTIVE PROFILE DRAWER / MODAL                       -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <v-dialog v-model="empDrawer" max-width="820px" scrollable>
      <div class="emp-drawer-content" v-if="empDrawer">
        <!-- Drawer Header -->
        <div class="ed-header">
          <div class="ed-profile">
            <div class="ed-avatar">{{ getInitials(empDetail.name) }}</div>
            <div>
              <div class="ed-name">
                {{ empDetail.name }}
                <span class="ed-id">{{ empDetail.employeeId ? '#' + empDetail.employeeId : '' }}</span>
                <span v-if="empDetail.onProbation" class="prob-tag">On Probation</span>
              </div>
              <div class="ed-meta">
                <span><v-icon size="14" color="#14663a">mdi-domain</v-icon> {{ empDetail.division || 'Unassigned' }}</span>
                <span><v-icon size="14" color="#14663a">mdi-badge-account-outline</v-icon> {{ empDetail.role || '—' }}</span>
                <span><v-icon size="14" color="#14663a">mdi-account-tie</v-icon> RM: {{ empDetail.manager || '—' }}</span>
              </div>
            </div>
          </div>
          <button class="ed-close" @click="empDrawer = false"><v-icon size="20">mdi-close</v-icon></button>
        </div>

        <!-- Drawer Body -->
        <div class="ed-body" v-if="empDetailLoading">
          <div class="drawer-loader"><v-progress-circular indeterminate color="#14663a" size="40" /><span>Loading 360° Profile...</span></div>
        </div>
        <div class="ed-body" v-else>
          <!-- Quick Stat Cards -->
          <div class="ed-stats-grid">
            <div class="ed-stat">
              <div class="eds-v" :style="{ color: scoreColor(empDetail.score) }">{{ empDetail.score }}<small>/100</small></div>
              <div class="eds-l">Effectiveness Score</div>
            </div>
            <div class="ed-stat">
              <div class="eds-v">{{ empDetail.metPercent }}%</div>
              <div class="eds-l">Target Achievement</div>
            </div>
            <div class="ed-stat">
              <div class="eds-v">{{ empDetail.pmrRating != null ? empDetail.pmrRating : '—' }}</div>
              <div class="eds-l">Appraisal PMR</div>
            </div>
            <div class="ed-stat">
              <div class="eds-v">{{ empDetail.loginDays || 0 }} <small>days</small></div>
              <div class="eds-l">Attendance Logins</div>
            </div>
            <div class="ed-stat pip" v-if="empPip && empPip.summary">
              <div class="eds-v">{{ empPip.summary.due ? empPip.summary.dueDeliveryRate + '%' : '—' }}</div>
              <div class="eds-l">PIP Delivered Till Date</div>
            </div>
          </div>

          <!-- Mapped Projects -->
          <div class="ed-section" v-if="empDetail.projects && empDetail.projects.length">
            <div class="ed-sec-title"><v-icon size="16" color="#14663a">mdi-compass-outline</v-icon> Mapped Conservation Projects</div>
            <div class="ed-proj-list">
              <span class="ed-proj-chip" v-for="(p, i) in empDetail.projects" :key="i">
                <b>{{ p.name }}</b> <span class="dim">({{ p.role }} · {{ p.location }})</span>
              </span>
            </div>
          </div>

          <div class="ed-section employee-pip-section" v-if="empPip && empPip.summary">
            <div class="ed-sec-title spread"><span><v-icon size="16" color="#14663a">mdi-clipboard-check-outline</v-icon> PIP Evidence (FY {{ empSelectedFY || selectedFY || financialYear }})</span><button class="drawer-link" @click="openEmployeePipDetail">Open full PIP <v-icon size="13">mdi-arrow-right</v-icon></button></div>
            <div v-if="empPip.records && empPip.records.length" class="employee-pip-summary">
              <div><strong>{{ empPip.summary.reviewItems || 0 }}</strong><span>Unique review items</span></div>
              <div><strong class="good-txt">{{ empPip.summary.met || 0 }}</strong><span>Met</span></div>
              <div><strong class="warn-txt">{{ empPip.summary.delayedMet || 0 }}</strong><span>Delayed met</span></div>
              <div><strong class="bad">{{ empPip.summary.notMet || 0 }}</strong><span>Not met</span></div>
              <div><strong class="overdue-txt">{{ empPip.summary.overdue || 0 }}</strong><span>Overdue</span></div>
              <div><strong>{{ empPip.summary.pending || 0 }}</strong><span>Future pending</span></div>
            </div>
            <div v-if="empPip.records && empPip.records.length" class="ed-proj-list pip-project-list">
              <span class="ed-proj-chip" v-for="project in employeePipProjects" :key="project.key" :class="{ unlinked: !project.linked }"><v-icon size="13" :color="project.linked ? '#14663a' : '#b56b00'">{{ project.linked ? 'mdi-link-variant' : 'mdi-link-variant-off' }}</v-icon><b>{{ project.name }}</b></span>
            </div>
            <div v-else class="employee-pip-none">No PIP evidence is mapped to this employee for the selected financial year.</div>
          </div>

          <!-- Full KPI Targets List -->
          <div class="ed-section">
            <div class="ed-sec-title"><v-icon size="16" color="#14663a">mdi-target</v-icon> KPI Targets Assigned (FY {{ empSelectedFY || selectedFY || financialYear }})</div>
            <table class="an-table ed-table" v-if="empTargets.length">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Target Objective</th>
                  <th class="r">Weight</th>
                  <th class="r">Score</th>
                  <th>Due Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(t, i) in empTargets" :key="i">
                  <td class="dim">{{ t.slNo || (i + 1) }}</td>
                  <td class="strong">{{ t.target }}</td>
                  <td class="r">{{ t.weightage }}</td>
                  <td class="r font-weight-bold">{{ t.score }}</td>
                  <td>{{ formatDate(t.targetDate) }}</td>
                  <td>
                    <span class="status-pill" :class="getTargetStatusClass(t)">
                      {{ t.status }}
                      <span v-if="t.delayedDays > 0" class="delay-tag">+{{ t.delayedDays }}d</span>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-else class="dim center" style="padding:24px;">No target sheet recorded for this employee in FY {{ empSelectedFY || selectedFY || financialYear }}.</div>
          </div>
        </div>

        <!-- Drawer Footer -->
        <div class="ed-footer">
          <div class="ed-contact" v-if="empDetail.email || empDetail.mobile">
            <span v-if="empDetail.email"><v-icon size="14" color="#6b7a72">mdi-email-outline</v-icon> {{ empDetail.email }}</span>
            <span v-if="empDetail.mobile"><v-icon size="14" color="#6b7a72">mdi-phone-outline</v-icon> {{ empDetail.mobile }}</span>
          </div>
          <button class="btn solid" @click="empDrawer = false">Close</button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

function empty() {
  return {
    adoption: { assigned: 0, submitted: 0, approved: 0, notStarted: 0, submissionRate: 0, approvalRate: 0, employees: 0 },
    workingDaysElapsed: 0, employees: [], divisions: [], managers: [], probationWatch: [],
  };
}

export default {
  name: "CeoAnalytics",
  data() {
    return {
      activeTab: "scorecards", // 'scorecards' | 'pip_comparison' | 'talent_matrix' | 'projects' | 'donations_yoy'
      loading: true,
      error: "",
      sourceFailures: [],
      financialYear: "",
      financialYearList: [],
      selectedFY: "",
      pmrAvailable: false,
      a: empty(),
      view: "top",
      search: "",
      divisionFilter: "",

      // 9-Box Talent Matrix
      talentData: null,
      talentSearch: "",
      talentDivFilter: "",
      selectedQuadrantKey: "",

      // Project Benchmarking
      projectList: [],
      projectSearch: "",
      projectDivFilter: "",
      projectFilterStatus: "all",

      // PIP executive comparisons
      pip: null,
      pipSort: "attention",
      pipDivisionSearch: "",
      pipStatusFilter: "all",

      // Multi-Year YoY Donations
      yoyData: null,
      yoyCurrency: "INR",

      // 360° Employee Drawer
      empDrawer: false,
      empDetailLoading: false,
      empDetail: {},
      empTargets: [],
      empSelectedFY: "",
      empPip: null,
      empDetailEmployeeId: "",

      wtiLogo: require("@/assets/images/wtilogo.png"),
    };
  },
  computed: {
    talentEmployeeRows() {
      if (!this.talentData) return [];
      const byId = new Map();
      Object.values(this.talentData.quadrants || {}).forEach((quadrant) => {
        (quadrant.emps || []).forEach((employee) => byId.set(String(employee.id), employee));
      });
      (this.talentData.dataGapEmployees || []).forEach((employee) => byId.set(String(employee.id), employee));
      return Array.from(byId.values());
    },
    activeEmployeeCount() {
      if (this.talentData && Number.isFinite(Number(this.talentData.eligibleEmployees))) return Number(this.talentData.eligibleEmployees);
      return this.talentEmployeeRows.length || Number(this.a.adoption.employees || 0);
    },
    kpiCoverageRate() {
      return this.activeEmployeeCount ? Math.round((Number(this.a.adoption.employees || 0) / this.activeEmployeeCount) * 1000) / 10 : 0;
    },
    divisionNames() {
      const employees = this.talentEmployeeRows.length ? this.talentEmployeeRows : (this.a.employees || []);
      return Array.from(new Set(employees.map((e) => e.division))).filter(Boolean).sort();
    },
    divisionScorecards() {
      const baseByName = new Map((this.a.divisions || []).map((row) => [this.normalizedName(row.division), row]));
      const pipByName = new Map(this.pipDivisions.map((row) => [this.normalizedName(row.name), row]));
      const staffByName = new Map();
      this.talentEmployeeRows.forEach((employee) => {
        const key = this.normalizedName(employee.division || 'Unassigned');
        if (!staffByName.has(key)) staffByName.set(key, { count: 0, attendanceTotal: 0, attendanceCount: 0, name: employee.division || 'Unassigned' });
        const row = staffByName.get(key);
        row.count += 1;
        if (employee.attendancePct != null && Number.isFinite(Number(employee.attendancePct))) {
          row.attendanceTotal += Number(employee.attendancePct);
          row.attendanceCount += 1;
        }
      });
      const keys = new Set([...baseByName.keys(), ...pipByName.keys(), ...staffByName.keys()]);
      return Array.from(keys).map((key) => {
        const base = baseByName.get(key) || null;
        const pip = pipByName.get(key) || null;
        const staff = staffByName.get(key) || null;
        const activeStaff = staff ? staff.count : Number((base && base.staff) || 0);
        const kpiStaff = Number((base && base.staff) || 0);
        const baseScore = base ? Number(base.score || 0) : null;
        const pipCounts = pip && pip.counts ? pip.counts : {};
        const pipIncluded = Number(pipCounts.due || 0) > 0;
        const pipDeliveryRate = pipIncluded ? Number(pipCounts.dueDeliveryRate || 0) : null;
        const executiveScore = baseScore == null ? null : (pipIncluded
          ? Math.round(((baseScore * 0.8) + (pipDeliveryRate * 0.2)) * 10) / 10
          : baseScore);
        return {
          division: (base && base.division) || (pip && pip.name) || (staff && staff.name) || 'Unassigned',
          activeStaff,
          kpiStaff,
          kpiCoverageRate: activeStaff ? Math.round((kpiStaff / activeStaff) * 1000) / 10 : 0,
          targets: Number((base && base.targets) || 0),
          baseScore,
          pipIncluded,
          pipDeliveryRate,
          pipDelivered: Number(pipCounts.delivered || 0),
          pipDue: Number(pipCounts.due || 0),
          executiveScore,
          avgAttendance: staff && staff.attendanceCount
            ? Math.round((staff.attendanceTotal / staff.attendanceCount) * 10) / 10
            : (base ? Number(base.avgAttendance || 0) : null),
          submissionRate: Number((base && base.submissionRate) || 0),
          notMet: Number((base && base.notMet) || 0),
          exits: Number((base && base.exits) || 0),
        };
      }).sort((left, right) => {
        if (left.executiveScore == null && right.executiveScore != null) return 1;
        if (right.executiveScore == null && left.executiveScore != null) return -1;
        return Number(right.executiveScore || 0) - Number(left.executiveScore || 0) || left.division.localeCompare(right.division);
      });
    },
    filteredEmployees() {
      const q = this.search.trim().toLowerCase();
      const employees = this.talentEmployeeRows.length ? this.talentEmployeeRows : (this.a.employees || []).map((employee) => Object.assign({}, employee, {
        performanceIndex: employee.score,
        pipDeliveryRate: null,
        evidenceCoverage: 0,
      }));
      return employees.filter((e) => {
        if (this.divisionFilter && e.division !== this.divisionFilter) return false;
        if (q && !((e.name || "").toLowerCase().includes(q) || String(e.employeeId || "").toLowerCase().includes(q))) return false;
        return true;
      });
    },
    shownEmployees() {
      const score = (employee) => employee.performanceIndex == null ? null : Number(employee.performanceIndex);
      const ranked = this.filteredEmployees.slice().sort((left, right) => {
        if (score(left) == null && score(right) != null) return 1;
        if (score(right) == null && score(left) != null) return -1;
        return Number(score(right) || 0) - Number(score(left) || 0) || left.name.localeCompare(right.name);
      });
      if (this.view === "top") return ranked.slice(0, 10);
      if (this.view === "attention") return ranked.filter((employee) => score(employee) != null).sort((left, right) => score(left) - score(right) || left.name.localeCompare(right.name)).slice(0, 10);
      return ranked;
    },
    selectedQuadrant() {
      if (!this.talentData || !this.talentData.quadrants || !this.selectedQuadrantKey) return null;
      return this.talentData.quadrants[this.selectedQuadrantKey] || null;
    },
    selectedQuadrantEmps() {
      if (!this.selectedQuadrantKey) return [];
      return this.filteredQuadrantEmps(this.selectedQuadrantKey);
    },
    talentDivisionNames() {
      if (!this.talentData) return [];
      const names = new Set();
      Object.values(this.talentData.quadrants || {}).forEach((quadrant) => (quadrant.emps || []).forEach((employee) => {
        if (employee.division) names.add(employee.division);
      }));
      (this.talentData.dataGapEmployees || []).forEach((employee) => { if (employee.division) names.add(employee.division); });
      return Array.from(names).sort();
    },
    filteredTalentGaps() {
      if (!this.talentData) return [];
      const q = this.talentSearch.trim().toLowerCase();
      return (this.talentData.dataGapEmployees || []).filter((employee) => {
        if (this.talentDivFilter && employee.division !== this.talentDivFilter) return false;
        if (q && !((employee.name || '').toLowerCase().includes(q) || String(employee.employeeId || '').toLowerCase().includes(q))) return false;
        return true;
      });
    },
    projectDivisions() {
      return Array.from(new Set((this.projectList || []).map((p) => p.division))).filter(Boolean).sort();
    },
    filteredProjects() {
      const q = this.projectSearch.trim().toLowerCase();
      return (this.projectList || []).filter((p) => {
        if (this.projectDivFilter && p.division !== this.projectDivFilter) return false;
        if (this.projectFilterStatus === 'attention' && !p.needsAttention) return false;
        if (this.projectFilterStatus === 'pip' && !p.pipLinked) return false;
        if (this.projectFilterStatus === 'no_evidence' && (Number(p.totalTargets || 0) > 0 || (p.pipLinked && Number(p.pipCounts && p.pipCounts.reviewItems || 0) > 0))) return false;
        const searchable = [p.projectName, p.code, p.location, p.division, p.projectHead]
          .concat((p.coPis || []).map((person) => person.name)).filter(Boolean).join(' ').toLowerCase();
        if (q && !searchable.includes(q)) return false;
        return true;
      });
    },
    projectPortfolio() {
      const projects = this.projectList || [];
      return {
        total: projects.length,
        withPi: projects.filter((project) => !!project.primaryPi).length,
        missingPi: projects.filter((project) => !project.primaryPi).length,
        staffed: projects.filter((project) => Number(project.teamSize || 0) > 0).length,
        unstaffed: projects.filter((project) => Number(project.teamSize || 0) <= 0).length,
        kpiEvidence: projects.filter((project) => Number(project.totalTargets || 0) > 0).length,
        pipLinked: projects.filter((project) => project.pipLinked).length,
        attention: projects.filter((project) => project.needsAttention).length,
      };
    },
    avgProjectTeamSize() {
      if (!this.projectList.length) return 0;
      const sum = this.projectList.reduce((acc, p) => acc + (p.teamSize || 0), 0);
      return Math.round((sum / this.projectList.length) * 10) / 10;
    },
    pipReady() { return !!(this.pip && this.pip.summary); },
    pipSummary() { return (this.pip && this.pip.summary) || {}; },
    pipDataStatus() { return (this.pip && this.pip.dataStatus) || {}; },
    pipStaffCoverageRate() {
      return this.talentData && this.activeEmployeeCount ? Math.round((Number(this.pipSummary.mappedEmployees || 0) / this.activeEmployeeCount) * 1000) / 10 : null;
    },
    pipScopeLabel() {
      const scope = (this.pip && this.pip.calculationScope) || {};
      const period = scope.periodLabel || "current reporting period";
      return scope.asOfDate ? period + " through " + this.formatDate(scope.asOfDate) : period;
    },
    pipDivisions() {
      const kpiByName = new Map((this.a.divisions || []).map((row) => [this.normalizedName(row.division), row]));
      const activeStaffByName = new Map();
      this.talentEmployeeRows.forEach((employee) => {
        const key = this.normalizedName(employee.division || 'Unassigned');
        activeStaffByName.set(key, (activeStaffByName.get(key) || 0) + 1);
      });
      return ((this.pip && this.pip.divisionAnalysis) || []).map((row) => Object.assign({}, row, {
        kpi: kpiByName.get(this.normalizedName(row.name)) || null,
        activeStaff: activeStaffByName.has(this.normalizedName(row.name)) ? activeStaffByName.get(this.normalizedName(row.name)) : null,
      }));
    },
    filteredPipDivisions() {
      const search = this.pipDivisionSearch.trim().toLowerCase();
      return this.pipDivisions.filter((row) => {
        if (search && !String(row.name || '').toLowerCase().includes(search) && !String(row.code || '').toLowerCase().includes(search)) return false;
        const attention = Number(row.counts.notMet || 0) + Number(row.counts.overdue || 0);
        if (this.pipStatusFilter === 'attention' && attention <= 0) return false;
        if (this.pipStatusFilter === 'due' && Number(row.counts.due || 0) <= 0) return false;
        if (this.pipStatusFilter === 'not_due' && Number(row.counts.due || 0) > 0) return false;
        return true;
      });
    },
    sortedPipDivisions() {
      const rows = this.filteredPipDivisions.slice();
      if (this.pipSort === "name") return rows.sort((a, b) => a.name.localeCompare(b.name));
      if (this.pipSort === "delivery") return rows.sort((a, b) => Number(b.counts.dueDeliveryRate || 0) - Number(a.counts.dueDeliveryRate || 0) || a.name.localeCompare(b.name));
      return rows.sort((a, b) => {
        const riskA = Number(a.counts.notMet || 0) + Number(a.counts.overdue || 0);
        const riskB = Number(b.counts.notMet || 0) + Number(b.counts.overdue || 0);
        return riskB - riskA || Number(a.counts.dueDeliveryRate || 0) - Number(b.counts.dueDeliveryRate || 0) || a.name.localeCompare(b.name);
      });
    },
    pipWatchlist() {
      return this.pipDivisions.slice().filter((row) => Number(row.counts.notMet || 0) + Number(row.counts.overdue || 0) > 0).sort((a, b) => {
        const riskA = Number(a.counts.notMet || 0) + Number(a.counts.overdue || 0);
        const riskB = Number(b.counts.notMet || 0) + Number(b.counts.overdue || 0);
        return riskB - riskA || Number(a.counts.dueDeliveryRate || 0) - Number(b.counts.dueDeliveryRate || 0) || a.name.localeCompare(b.name);
      }).slice(0, 3);
    },
    employeePipProjects() {
      const rows = (this.empPip && this.empPip.records) || [];
      const projects = new Map();
      rows.forEach((record) => {
        const linked = record.linkedProject || null;
        const key = linked && linked.id ? String(linked.id) : String(record.source || "") + "|" + String(record.sheetName || "");
        if (!projects.has(key)) projects.set(key, { key, name: linked && linked.name ? linked.name : (record.sheetName || "Unnamed PIP project"), linked: !!linked });
      });
      return Array.from(projects.values()).sort((a, b) => a.name.localeCompare(b.name));
    },
    maxYoyMonthlyValue() {
      if (!this.yoyData || !this.yoyData.comparison) return 1;
      let max = 1;
      this.yoyData.comparison.forEach((fy) => {
        (fy.months || []).forEach((m) => {
          const value = this.yoyCurrency === "USD" ? Number(m.usd || 0) : Number(m.inr || 0);
          if (value > max) max = value;
        });
      });
      return max;
    },
    yoyHasQualityWarnings() {
      const quality = this.yoyData && this.yoyData.dataQuality;
      return !!quality && (Number(quality.unsupportedCurrencyRecords || 0) > 0 || Number(quality.invalidAmountRecords || 0) > 0 || Number(quality.futureDatedSelectedFyRecords || 0) > 0);
    },
    printDate() {
      return new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    },
  },
  mounted() {
    if (this.$route.query.tab) {
      this.activeTab = this.$route.query.tab;
    }
    this.fetchFinancialYears();
    this.fetchAllData();
  },
  methods: {
    authHeaders() { return { "x-auth-token": localStorage.getItem("token") }; },
    switchTab(tab) {
      this.activeTab = tab;
      if (this.$route.query.tab !== tab) {
        this.$router.replace({ query: { ...this.$route.query, tab } }).catch(() => {});
      }
    },
    onFYChange() {
      this.fetchAllData();
    },
    async fetchFinancialYears() {
      try {
        const res = await axios.get("dashboard/ceo/financial-years", { headers: this.authHeaders() });
        if (res.data && res.data.status && res.data.data) {
          this.financialYearList = res.data.data;
          if (!this.selectedFY) this.selectedFY = res.data.current || res.data.data[0];
        }
      } catch (e) {
        this.financialYearList = [];
      }
    },
    async fetchAllData() {
      this.loading = true;
      this.error = "";
      this.sourceFailures = [];
      this.a = empty();
      this.talentData = null;
      this.projectList = [];
      this.pip = null;
      this.yoyData = null;
      const params = this.selectedFY ? { financialYear: this.selectedFY } : {};
      try {
        const [resAnalytics, resTalent, resProjects, resYoy, resPip] = await Promise.all([
          axios.get("dashboard/ceo/analytics", { headers: this.authHeaders(), params }).catch(() => ({ data: null })),
          axios.get("dashboard/ceo/talent-matrix", { headers: this.authHeaders(), params }).catch(() => ({ data: null })),
          axios.get("dashboard/ceo/projects-benchmark", { headers: this.authHeaders(), params }).catch(() => ({ data: null })),
          axios.get("dashboard/ceo/donations-yoy", { headers: this.authHeaders(), params }).catch(() => ({ data: null })),
          axios.get("/pip/admin/dashboard", { headers: this.authHeaders(), params: { year: this.selectedFY || undefined } }).catch(() => ({ data: null })),
        ]);

        const sourceChecks = [
          ["KPI analytics", resAnalytics], ["talent matrix", resTalent], ["project benchmark", resProjects],
          ["donation comparison", resYoy], ["PIP evidence", resPip],
        ];
        this.sourceFailures = sourceChecks.filter(([, response]) => !(response.data && response.data.status)).map(([label]) => label);
        if (this.sourceFailures.length) this.error = "Data unavailable from: " + this.sourceFailures.join(", ") + ". Other sections remain current.";

        if (resAnalytics.data && resAnalytics.data.status) {
          this.a = Object.assign(empty(), resAnalytics.data.data);
          this.financialYear = resAnalytics.data.financialYear || "";
          if (!this.selectedFY) this.selectedFY = this.financialYear;
          this.pmrAvailable = !!resAnalytics.data.pmrAvailable;
        }

        if (resTalent.data && resTalent.data.status) {
          this.talentData = resTalent.data.data;
        }

        if (resProjects.data && resProjects.data.status) {
          this.projectList = resProjects.data.data || [];
        }

        if (resYoy.data && resYoy.data.status) {
          this.yoyData = resYoy.data.data;
        }

        this.pip = resPip.data && resPip.data.status ? resPip.data : null;
      } catch (e) {
        this.error = "Unable to reach the server.";
      } finally {
        this.loading = false;
      }
    },
    async openEmployee360(employeeId) {
      if (!employeeId) return;
      this.empDrawer = true;
      this.empDetailLoading = true;
      this.empDetail = {};
      this.empTargets = [];
      this.empPip = null;
      this.empDetailEmployeeId = employeeId;
      try {
        const year = this.selectedFY || this.financialYear;
        const responses = await Promise.all([
          axios.get("dashboard/ceo/employee-target-detail", { headers: this.authHeaders(), params: { employeeId, financialYear: year } }),
          axios.get("/pip/employee/" + employeeId, { headers: this.authHeaders(), params: { year } }).catch(() => ({ data: null })),
        ]);
        const res = responses[0];
        if (res.data && res.data.status) {
          const scoreRow = (this.a.employees || []).find((row) => String(row.id) === String(employeeId));
          this.empDetail = Object.assign({}, scoreRow || {}, res.data.employee || {});
          this.empTargets = res.data.targets || [];
          this.empSelectedFY = res.data.financialYear || "";
        }
        this.empPip = responses[1].data && responses[1].data.status ? responses[1].data : null;
      } catch (e) {
        this.empDetail = { name: "Error loading profile" };
      } finally {
        this.empDetailLoading = false;
      }
    },
    filteredQuadrantEmps(quadrantKey) {
      if (!this.talentData || !this.talentData.quadrants || !this.talentData.quadrants[quadrantKey]) return [];
      const q = this.talentSearch.trim().toLowerCase();
      return (this.talentData.quadrants[quadrantKey].emps || []).filter((e) => {
        if (this.talentDivFilter && e.division !== this.talentDivFilter) return false;
        if (q && !((e.name || "").toLowerCase().includes(q) || String(e.employeeId || "").toLowerCase().includes(q))) return false;
        return true;
      });
    },
    getQuadrantCount(keys) {
      if (!this.talentData || !this.talentData.quadrants) return 0;
      return keys.reduce((acc, k) => acc + (this.filteredQuadrantEmps(k).length || 0), 0);
    },
    talentMetric(value) { return value == null || value === '' ? '—' : `${value}%`; },
    selectQuadrant(key) {
      this.selectedQuadrantKey = this.selectedQuadrantKey === key ? "" : key;
    },
    normalizedName(value) { return String(value || "").trim().replace(/\s+/g, " ").toLowerCase(); },
    deliveryColor(value) { return Number(value || 0) >= 70 ? "#2e7d32" : Number(value || 0) >= 40 ? "#e08600" : "#c62828"; },
    openPipDivision(row) {
      if (!row) return;
      this.$router.push({ path: "/ceo-pip", query: { year: this.selectedFY || this.financialYear, tab: "divisions", openDivision: row.id } });
    },
    openProjectBenchmark(project) {
      if (!project) return;
      this.$router.push({ path: '/ceo-pip', query: { year: this.selectedFY || this.financialYear, tab: 'projects', projectId: project.id } });
    },
    openEmployeePipDetail() {
      if (!this.empDetailEmployeeId) return;
      this.$router.push({ path: "/ceo-pip", query: { year: this.empSelectedFY || this.selectedFY || this.financialYear, tab: "employees", employeeId: this.empDetailEmployeeId } });
    },
    getBarHeight(amt) {
      if (!amt || this.maxYoyMonthlyValue <= 0) return 4;
      return Math.max(6, Math.min(100, Math.round((amt / this.maxYoyMonthlyValue) * 100)));
    },
    getYoyMonthValue(financialYear, index) {
      const month = financialYear && financialYear.months ? financialYear.months[index] : null;
      if (!month) return 0;
      return this.yoyCurrency === "USD" ? Number(month.usd || 0) : Number(month.inr || 0);
    },
    formatYoyValue(value) {
      return this.yoyCurrency === "USD" ? this.formatUsd(value) : this.formatMoney(value);
    },
    getTargetStatusClass(t) {
      if (t.status === "Met") return "top";
      if (t.status === "Delayed Met") return "moderate";
      return "lagging";
    },
    getInitials(name) {
      if (!name) return "WT";
      const parts = name.trim().split(" ");
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.substring(0, 2).toUpperCase();
    },
    formatDate(d) {
      if (!d) return "—";
      try {
        return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
      } catch (e) { return "—"; }
    },
    formatDateTime(d) {
      if (!d) return "—";
      try { return new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
      catch (e) { return "—"; }
    },
    exportReport() {
      window.print();
    },
    scoreColor(s) { return s >= 70 ? "#2e7d32" : s >= 40 ? "#e08600" : "#c62828"; },
    goBack() { this.$router.push("/ceo-dashboard"); },
    logout() { this.$store.commit("logoutUser"); },
    num(n) { return Number(n || 0).toLocaleString("en-IN"); },
    formatMoney(n) { return "₹" + Number(n || 0).toLocaleString("en-IN"); },
    formatUsd(n) { return "$" + Number(n || 0).toLocaleString("en-US"); },
  },
};
</script>

<style scoped>
.an-root { min-height: 100vh; width: 100%; background: linear-gradient(180deg, #f3f7f4 0%, #e9f0eb 100%); font-family: "Poppins", "Segoe UI", system-ui, sans-serif; color: #1b2320; }
.an-topbar { position: sticky; top: 0; z-index: 20; display: flex; align-items: center; justify-content: space-between; padding: 12px 26px; background: rgba(255,255,255,0.96); backdrop-filter: saturate(180%) blur(12px); border-bottom: 1px solid rgba(20,102,58,0.12); }
.brand { display: flex; align-items: center; gap: 12px; }
.back-btn { background: #fff; border: 1px solid rgba(20,102,58,0.2); border-radius: 10px; width: 38px; height: 38px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.back-btn:hover { background: #eef6f0; }
.brand-logo-box { width: 46px; height: 46px; border-radius: 12px; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #14663a, #2e7d32); box-shadow: 0 6px 16px rgba(20,102,58,0.3); padding: 6px; }
.brand-logo { max-width: 100%; max-height: 100%; object-fit: contain; }
.brand-title { font-size: 17px; font-weight: 700; }
.brand-sub { font-size: 11.5px; color: #6b7a72; margin-top: -2px; }
.topbar-actions { display: flex; align-items: center; gap: 12px; }

/* Financial Year Selector */
.fy-picker { display: flex; align-items: center; gap: 6px; background: #eef6f0; border: 1px solid #cfe5d8; padding: 4px 10px; border-radius: 12px; }
.fy-select { font-family: inherit; font-size: 12.5px; font-weight: 700; color: #14663a; background: transparent; border: none; outline: none; cursor: pointer; }
.fy-chip { font-size: 12px; font-weight: 700; color: #14663a; background: #e5f1ea; border: 1px solid #cfe5d8; padding: 5px 12px; border-radius: 20px; }

.btn { display: inline-flex; align-items: center; gap: 7px; border: none; cursor: pointer; font-size: 13px; font-weight: 600; padding: 8px 14px; border-radius: 10px; font-family: inherit; transition: all 0.2s; }
.btn.small { padding: 6px 10px; font-size: 12px; }
.btn.ghost { background: #fff; color: #14663a; border: 1px solid rgba(20,102,58,0.2); }
.btn.ghost:hover { background: #f0f7f2; }
.btn.solid { background: linear-gradient(135deg, #14663a, #2e7d32); color: #fff; }
.spin { animation: spin 0.9s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }
.loader-wrap { display: flex; justify-content: center; align-items: center; min-height: 70vh; }

/* Navigation Tab Bar */
.an-nav { background: #fff; border-bottom: 1px solid #e1ebe4; padding: 0 26px; }
.nav-tabs { max-width: 1280px; margin: 0 auto; display: flex; gap: 8px; overflow-x: auto; }
.nav-tab { display: inline-flex; align-items: center; gap: 8px; background: transparent; border: none; border-bottom: 3px solid transparent; padding: 14px 16px; font-family: inherit; font-size: 13.5px; font-weight: 600; color: #6b7a72; cursor: pointer; white-space: nowrap; transition: all 0.2s; }
.nav-tab:hover { color: #14663a; background: #f7faf8; }
.nav-tab.active { color: #14663a; border-bottom-color: #14663a; }
.nav-tab .badge { background: #e8f3ec; color: #14663a; font-size: 11px; font-weight: 700; padding: 2px 7px; border-radius: 10px; }

.an-body { max-width: 1280px; margin: 0 auto; padding: 22px 26px 44px; }
.banner.error { display: flex; gap: 8px; background: #fdecec; color: #c62828; border: 1px solid #f5c6c6; padding: 10px 14px; border-radius: 12px; margin-bottom: 16px; font-size: 13px; }
.executive-scope { display: grid; grid-template-columns: 1.15fr repeat(4, 1fr); background: #fff; border: 1px solid #dfe9e3; border-radius: 15px; box-shadow: 0 3px 14px rgba(20,40,30,.04); overflow: hidden; margin-bottom: 20px; }
.scope-intro, .scope-item { min-height: 76px; padding: 13px 15px; display: flex; flex-direction: column; justify-content: center; border-right: 1px solid #e8efeb; }
.scope-item:last-child { border-right: 0; }
.scope-intro { background: linear-gradient(135deg, #115d35, #23794a); color: #fff; }
.scope-intro span, .scope-item span { font-size: 9.5px; font-weight: 800; letter-spacing: .45px; text-transform: uppercase; opacity: .78; }
.scope-intro strong { font-size: 17px; margin-top: 3px; }
.scope-item strong { color: #183728; font-size: 17px; margin-top: 2px; }
.scope-item small { color: #839188; font-size: 9.5px; margin-top: 2px; }
.section-eyebrow { display: flex; align-items: center; gap: 7px; font-size: 12.5px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; color: #14663a; margin: 18px 2px 12px; }
.section-eyebrow.compact { margin-top: 20px; }
.eye-sub { text-transform: none; letter-spacing: 0; font-weight: 500; color: #8a978f; font-size: 11.5px; margin-left: 6px; }
.insight { display: flex; gap: 8px; align-items: flex-start; background: #fdf6ea; border: 1px solid #f2ddb8; color: #7a5a1e; padding: 11px 14px; border-radius: 12px; font-size: 13px; margin-bottom: 14px; }
.insight b { color: #5a4110; }
.scorecard-methodology { display: grid; grid-template-columns: 1.2fr 1fr 1fr; gap: 10px; margin-bottom: 14px; }
.scorecard-methodology > div { display: flex; align-items: flex-start; gap: 9px; padding: 12px 14px; border: 1px solid #dce8e0; border-radius: 12px; background: #fff; color: #617168; font-size: 11px; line-height: 1.45; }
.scorecard-methodology strong { display: block; color: #233d30; font-size: 12px; margin-bottom: 2px; }

.funnel { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; background: #fff; border: 1px solid #edf2ee; border-radius: 16px; padding: 18px 20px; box-shadow: 0 3px 14px rgba(20,40,30,0.05); }
.fn-step { flex: 1; min-width: 120px; text-align: center; }
.fn-step.warn { background: #fdf3e3; border-radius: 12px; padding: 6px; }
.fn-v { font-size: 26px; font-weight: 700; color: #14432a; }
.fn-l { font-size: 12px; color: #6b7a72; margin-top: 2px; } .fn-l em { font-style: normal; color: #14663a; font-weight: 700; }

.card { background: #fff; border-radius: 16px; box-shadow: 0 3px 14px rgba(20,40,30,0.05); border: 1px solid #edf2ee; margin-bottom: 16px; }
.table-card { overflow-x: auto; }
.an-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.an-table thead th { text-align: left; background: #f7faf8; color: #8a978f; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; padding: 10px 14px; white-space: nowrap; }
.an-table td { padding: 10px 14px; border-top: 1px solid #f1f4f2; color: #33403a; }
.an-table .r { text-align: right; }
.scorecard-table { min-width: 1480px; }
.employee-evidence-table { min-width: 1320px; }
.strong { font-weight: 600; color: #23302a; }
.dim { color: #9aa8a1; }
.manager-pip-meta { display: block; margin-top: 2px; color: #9aa8a1; font-size: 10px; line-height: 1.3; white-space: nowrap; }
.evidence-gap-label { display: block; max-width: 180px; margin-top: 3px; color: #a36a13; font-size: 9px; line-height: 1.25; white-space: normal; }
.bad { color: #c62828; font-weight: 700; }
.warn-txt { color: #b56b00; font-weight: 700; }
.center { text-align: center; }
.proj-chip { font-size: 11px; background: #eef6f0; color: #14663a; padding: 3px 9px; border-radius: 12px; white-space: nowrap; }
.fr-tag { font-size: 10px; font-weight: 700; color: #1565c0; background: #e3f2fd; padding: 2px 6px; border-radius: 8px; margin-left: 6px; }
.prob-tag { font-size: 10px; font-weight: 700; color: #b56b00; background: #fbf1e2; padding: 1px 7px; border-radius: 10px; margin-left: 6px; }
.score-cell { display: flex; align-items: center; gap: 8px; min-width: 120px; }
.score-bar { flex: 1; height: 8px; background: #eef2f0; border-radius: 5px; overflow: hidden; }
.score-fill { height: 100%; border-radius: 5px; }
.score-num { font-weight: 700; font-size: 12.5px; width: 34px; text-align: right; }

/* PIP executive comparison */
.pip-unavailable { display: flex; align-items: center; gap: 12px; padding: 22px; color: #65756c; }
.pip-unavailable div { display: flex; flex-direction: column; gap: 3px; }
.pip-unavailable strong { color: #33483d; font-size: 14px; }
.pip-unavailable span { font-size: 11.5px; }
.pip-methodology { display: grid; grid-template-columns: 1.05fr 1fr 1fr; gap: 10px; margin-bottom: 14px; }
.pip-methodology > div { display: flex; align-items: flex-start; gap: 9px; padding: 12px 14px; border: 1px solid #dce8e0; border-radius: 12px; background: #fff; color: #617168; font-size: 11px; line-height: 1.45; }
.pip-methodology strong { display: block; color: #233d30; font-size: 12px; margin-bottom: 2px; }
.pip-exec-grid { display: grid; grid-template-columns: repeat(3, minmax(180px, 1fr)); gap: 12px; margin-bottom: 15px; }
.pip-exec-card { min-height: 112px; display: flex; flex-direction: column; justify-content: center; background: #fff; border: 1px solid #e1eae5; border-top: 4px solid #789989; border-radius: 14px; padding: 14px 16px; box-shadow: 0 3px 12px rgba(20,40,30,.035); }
.pip-exec-card.featured { border-top-color: #14663a; background: linear-gradient(145deg, #fff, #f2f8f4); }
.pip-exec-card.risk { border-top-color: #c62828; }
.pip-exec-card.future { border-top-color: #7895a2; }
.pip-exec-card > span { color: #6f7e76; font-size: 10px; font-weight: 800; letter-spacing: .35px; text-transform: uppercase; }
.pip-exec-card > strong { color: #183728; font-size: 27px; line-height: 1.1; margin: 5px 0 3px; }
.pip-exec-card.featured > strong { color: #14663a; }
.pip-exec-card.risk > strong { color: #c62828; }
.pip-exec-card small { color: #87948d; font-size: 10.5px; line-height: 1.35; }
.pip-watch-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 6px; }
.pip-watch-card { padding: 14px 15px; border: 1px solid #ecd6d6; border-left: 4px solid #c62828; border-radius: 12px; background: #fff; text-align: left; font-family: inherit; cursor: pointer; transition: transform .15s, box-shadow .15s; }
.pip-watch-card:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(70,30,30,.08); }
.pip-watch-top, .pip-watch-foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.pip-watch-top span { color: #263c31; font-size: 12px; font-weight: 750; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pip-watch-top strong { color: #c62828; font-size: 13px; }
.pip-watch-bar { height: 6px; border-radius: 4px; overflow: hidden; background: #f2e6e6; margin: 9px 0 7px; }
.pip-watch-bar span { display: block; height: 100%; border-radius: inherit; background: linear-gradient(90deg, #c62828, #e57b45); }
.pip-watch-foot { color: #8b7777; font-size: 9.5px; }
.pip-table-filters { flex-wrap: wrap; justify-content: flex-end; }
.pip-table-filters .search-box { min-width: 190px; }
.delivery-formula { display: block; margin-top: 3px; color: #87948d; font-size: 9.5px; white-space: nowrap; }
.pip-compare-table td { padding-top: 12px; padding-bottom: 12px; }
.pip-compare-table .score-num { width: 48px; }
.pip-division-name { display: grid; grid-template-columns: 1fr auto; gap: 1px 8px; align-items: center; min-width: 170px; }
.pip-division-name small { grid-column: 1; color: #89968f; font-size: 9.5px; font-weight: 600; }
.pip-division-name .v-icon { grid-column: 2; grid-row: 1 / span 2; }
.good-txt { color: #2e7d32 !important; font-weight: 700; }
.overdue-txt { color: #e05d2f !important; font-weight: 700; }
.data-health { display: flex; align-items: center; gap: 7px; color: #748279; font-size: 10.5px; padding: 2px 4px 10px; }

/* Clickable Employee Row */
.clickable-row { cursor: pointer; transition: background 0.15s; }
.clickable-row:hover { background: #f4faf6; }
.emp-link { display: flex; align-items: center; justify-content: space-between; }
.hover-icon { opacity: 0; transition: opacity 0.15s; }
.clickable-row:hover .hover-icon { opacity: 1; }

.controls { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; margin-bottom: 12px; }
.view-toggle { display: flex; background: #fff; border: 1px solid #dcebe1; border-radius: 10px; overflow: hidden; }
.vt { border: none; background: transparent; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600; color: #5a6b62; padding: 9px 16px; }
.vt.on { background: #14663a; color: #fff; }
.filters { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.search-box { display: flex; align-items: center; gap: 6px; background: #fff; border: 1px solid #dcebe1; border-radius: 10px; padding: 7px 12px; }
.search-box input { border: none; outline: none; font-family: inherit; font-size: 13px; background: transparent; width: 180px; color: #23302a; }
.sel { font-family: inherit; font-size: 13px; color: #23302a; background: #fff; border: 1px solid #dcebe1; border-radius: 10px; padding: 8px 12px; cursor: pointer; }

/* 9-Box Matrix Styles */
.talent-methodology { display: grid; grid-template-columns: 1.2fr 1fr 1fr; gap: 10px; margin-bottom: 14px; }
.talent-methodology > div { display: flex; align-items: flex-start; gap: 9px; padding: 12px 14px; border: 1px solid #dce8e0; border-radius: 12px; background: #fff; color: #617168; font-size: 11px; line-height: 1.45; }
.talent-methodology strong { display: block; color: #233d30; font-size: 12px; margin-bottom: 2px; }
.matrix-overview-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 14px; }
.mo-card { background: #fff; border-radius: 14px; padding: 16px; border: 1px solid #edf2ee; box-shadow: 0 2px 10px rgba(0,0,0,0.03); }
.mo-card.population { border-left: 4px solid #546e7a; }
.mo-card.star { border-left: 4px solid #2e7d32; }
.mo-card.core { border-left: 4px solid #1565c0; }
.mo-card.risk { border-left: 4px solid #c62828; }
.mo-card.unrated { border-left: 4px solid #7b8790; }
.mo-val { font-size: 24px; font-weight: 700; color: #14432a; }
.mo-title { font-size: 13px; font-weight: 700; color: #23302a; margin-top: 2px; }
.mo-sub { font-size: 11px; color: #7a8880; }

.matrix-legend { display: flex; align-items: center; gap: 14px; font-size: 12px; color: #5a6b62; }
.leg-item { display: flex; align-items: center; gap: 5px; font-weight: 600; }
.leg-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.leg-dot.green { background: #2e7d32; }
.leg-dot.blue { background: #1565c0; }
.leg-dot.red { background: #c62828; }

.matrix-container { padding: 20px; display: flex; gap: 12px; align-items: stretch; }
.matrix-y-axis-label { writing-mode: vertical-rl; transform: rotate(180deg); font-size: 12px; font-weight: 700; color: #6b7a72; text-align: center; letter-spacing: 0.5px; }
.matrix-wrapper { flex: 1; }
.matrix-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.matrix-cell { background: #fbfdfc; border: 1px solid #dce8e0; border-radius: 12px; padding: 14px; min-height: 145px; cursor: pointer; transition: all 0.2s; }
.matrix-cell:hover { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(0,0,0,0.08); border-color: #14663a; }
.matrix-cell.selected { border: 2px solid #14663a; background: #eef6f0; }
.mc-head { display: flex; align-items: center; justify-content: space-between; }
.mc-tag { font-size: 11px; font-weight: 700; padding: 2px 7px; border-radius: 8px; }
.mc-tag.green { background: #e8f5e9; color: #2e7d32; }
.mc-tag.blue { background: #e3f2fd; color: #1565c0; }
.mc-tag.orange { background: #fff3e0; color: #e65100; }
.mc-tag.red { background: #ffebee; color: #c62828; }
.mc-count { font-size: 18px; font-weight: 800; color: #14432a; }
.mc-desc { font-size: 11px; color: #7a8880; margin-top: 8px; line-height: 1.3; }
.mc-people { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 11px; }
.mc-people span { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; border: 1px solid #dce8e0; border-radius: 999px; padding: 3px 7px; background: #fff; color: #40554a; font-size: 9.5px; }
.mc-people small { padding: 3px 4px; color: #718078; font-size: 9.5px; }
.matrix-x-axis-label { font-size: 12px; font-weight: 700; color: #6b7a72; text-align: center; margin-top: 10px; letter-spacing: 0.5px; }

.quadrant-detail-box { padding: 18px 20px; border-left: 4px solid #14663a; }
.qd-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px; }
.qd-title { font-size: 16px; font-weight: 700; color: #14432a; display: flex; align-items: center; gap: 7px; }
.qd-sub { font-size: 12px; color: #6b7a72; margin-top: 2px; }
.talent-table-wrap { overflow-x: auto; }
.talent-table-wrap .an-table { min-width: 1120px; }
.performance-score { display: inline-block; padding: 4px 7px; border-radius: 8px; background: #e8f3ec; color: #155f3a; }
.evidence-pill { display: inline-block; min-width: 42px; padding: 4px 7px; border-radius: 999px; background: #eef2f0; color: #52645a; font-weight: 700; }
.talent-gaps-card { margin-top: 16px; padding: 18px 20px; border-left: 4px solid #d18c14; }
.gap-count { padding: 5px 9px; border-radius: 999px; background: #fff4d7; color: #8a5c00; font-size: 10px; font-weight: 800; }
.gap-reason { display: inline-block; margin: 0 4px 4px 0; padding: 4px 7px; border-radius: 8px; background: #fff4d7; color: #8a5c00; font-size: 10px; font-weight: 700; }

/* Project Benchmarking */
.project-methodology { display: grid; grid-template-columns: 1.1fr 1fr 1fr; gap: 10px; margin-bottom: 14px; }
.project-methodology > div { display: flex; align-items: flex-start; gap: 9px; padding: 12px 14px; border: 1px solid #dce8e0; border-radius: 12px; background: #fff; color: #617168; font-size: 11px; line-height: 1.45; }
.project-methodology strong { display: block; color: #233d30; font-size: 12px; margin-bottom: 2px; }
.proj-kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 14px; }
.pk-card { background: #fff; border-radius: 14px; padding: 16px; border: 1px solid #edf2ee; }
.pk-card.top { border-left: 4px solid #2e7d32; }
.pk-card.warn { border-left: 4px solid #c62828; }
.pk-val { font-size: 24px; font-weight: 700; color: #14432a; }
.pk-lbl { font-size: 12px; color: #6b7a72; margin-top: 2px; }
.status-pill { font-size: 10.5px; font-weight: 700; padding: 3px 8px; border-radius: 10px; display: inline-flex; align-items: center; gap: 4px; }
.status-pill.top { background: #e8f5e9; color: #2e7d32; }
.status-pill.lagging { background: #ffebee; color: #c62828; }
.status-pill.moderate { background: #e3f2fd; color: #1565c0; }
.status-pill.unrated { background: #edf1f3; color: #65727a; }
.delay-tag { font-size: 9px; background: rgba(198,40,40,0.12); color: #c62828; padding: 1px 4px; border-radius: 4px; }
.project-benchmark-table { min-width: 1780px; }
.project-code, .project-location, .external-pi { display: block; margin-top: 3px; color: #89968f; font-size: 9.5px; font-weight: 600; }
.external-pi { color: #6a4ca3; }
.co-pi-list { display: flex; flex-direction: column; gap: 3px; min-width: 130px; }
.co-pi-list small { color: #53645b; font-size: 10px; }
.project-gap-pill { display: inline-block; margin: 0 4px 4px 0; padding: 3px 7px; border-radius: 8px; background: #fff4d7; color: #8a5c00; font-size: 9.5px; font-weight: 700; white-space: nowrap; }

/* Multi-Year YoY Donations */
.yoy-methodology { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 14px; }
.yoy-methodology > div { display: flex; align-items: flex-start; gap: 9px; padding: 12px 14px; border: 1px solid #dce8e0; border-radius: 12px; background: #fff; color: #617168; font-size: 11px; line-height: 1.45; }
.yoy-methodology strong { display: block; color: #233d30; font-size: 12px; margin-bottom: 2px; }
.yoy-quality { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; padding: 10px 14px; border: 1px solid #ead7a4; border-radius: 12px; background: #fff8e8; color: #765314; font-size: 11px; }
.yoy-summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 14px; }
.yoy-card { background: #fff; border-radius: 14px; padding: 16px; border: 1px solid #edf2ee; }
.yoy-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.yoy-fy { font-size: 12px; font-weight: 700; color: #14663a; background: #e5f1ea; padding: 2px 8px; border-radius: 10px; }
.yoy-tx { font-size: 11px; color: #7a8880; }
.yoy-val { font-size: 22px; font-weight: 800; color: #14432a; }
.yoy-sub { font-size: 11.5px; color: #6b7a72; margin-top: 2px; }
.yoy-foot { font-size: 11px; color: #8a978f; margin-top: 4px; border-top: 1px dashed #e5ede7; padding-top: 4px; }

.yoy-growth-card { background: #fff; border-radius: 14px; padding: 16px; border: 1px solid #edf2ee; }
.yoy-growth-card.pos { border-left: 4px solid #2e7d32; }
.yoy-growth-card.neg { border-left: 4px solid #c62828; }
.yoy-growth-card.neutral { border-left: 4px solid #819087; }
.yg-lbl { font-size: 11.5px; font-weight: 600; color: #6b7a72; }
.yg-val { font-size: 24px; font-weight: 800; display: flex; align-items: center; gap: 4px; margin-top: 4px; color: #14432a; }
.yg-sub { font-size: 11px; color: #8a978f; margin-top: 2px; }
.yoy-formula { margin-top: 7px; padding: 6px 8px; border-radius: 8px; background: #f3f7f4; color: #52645a; font-size: 9.5px; line-height: 1.35; }
.yoy-comparison-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; }
.yoy-comparison-grid > div { min-width: 0; padding: 12px 14px; border: 1px solid #dfe9e2; border-radius: 12px; background: #fff; display: flex; flex-direction: column; gap: 3px; }
.yoy-comparison-grid span { color: #6c7c73; font-size: 10px; font-weight: 700; }
.yoy-comparison-grid strong { color: #243d31; font-size: 17px; }
.yoy-comparison-grid strong.positive { color: #27783d; }
.yoy-comparison-grid strong.negative { color: #bd382f; }
.yoy-comparison-grid small { color: #929e97; font-size: 9px; line-height: 1.35; }

.yoy-chart-card { padding: 20px; }
.chart-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 12px; }
.chart-title { font-size: 15px; font-weight: 700; color: #14432a; display: flex; align-items: center; gap: 6px; }
.chart-sub { font-size: 11.5px; color: #6b7a72; }
.chart-legends { display: flex; align-items: center; gap: 14px; font-size: 11.5px; font-weight: 600; }
.yoy-chart-actions { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; }
.yoy-currency-toggle { display: inline-flex; padding: 2px; border: 1px solid #cddfd3; border-radius: 9px; background: #f5f8f6; }
.yoy-currency-toggle button { border: 0; border-radius: 7px; background: transparent; color: #66766d; padding: 5px 12px; font: inherit; font-size: 10px; font-weight: 800; cursor: pointer; }
.yoy-currency-toggle button.active { background: #14663a; color: #fff; box-shadow: 0 2px 6px rgba(20,102,58,0.18); }
.cleg { display: flex; align-items: center; gap: 5px; color: #43544c; }
.cleg-box { width: 12px; height: 12px; border-radius: 3px; }
.cleg-box.c1 { background: #14663a; }
.cleg-box.c2 { background: #43a047; }
.cleg-box.c3 { background: #a5d6a7; }

.chart-bars-wrap { display: flex; justify-content: space-between; align-items: flex-end; height: 180px; padding: 10px 0; border-bottom: 2px solid #e1ebe4; }
.month-col { flex: 1; display: flex; flex-direction: column; align-items: center; height: 100%; justify-content: flex-end; }
.bars-group { display: flex; align-items: flex-end; gap: 3px; height: 140px; }
.bar { width: 10px; border-radius: 3px 3px 0 0; transition: height 0.4s; }
.bar.bar-c1 { background: #14663a; }
.bar.bar-c2 { background: #43a047; }
.bar.bar-c3 { background: #a5d6a7; }
.month-label { font-size: 11px; font-weight: 600; color: #6b7a72; margin-top: 8px; }
.yoy-audit-card { margin-top: 16px; padding: 18px 20px; }
.yoy-audit-table { min-width: 980px; }
.audit-period { display: block; color: #89968f; font-size: 9px; margin-top: 2px; }

.grid { display: grid; gap: 16px; }
.grid-2 { grid-template-columns: repeat(2, 1fr); }
.panel { background: #fff; border-radius: 14px; padding: 18px; border: 1px solid #edf2ee; box-shadow: 0 2px 10px rgba(0,0,0,0.03); }
.panel-title { font-size: 14px; font-weight: 700; color: #14432a; display: flex; align-items: center; gap: 6px; }
.cat-list { list-style: none; padding: 0; margin: 12px 0 0; }
.cat-list li { display: flex; justify-content: space-between; font-size: 12.5px; padding: 7px 0; border-bottom: 1px solid #f1f5f2; }
.cat-name { color: #33403a; font-weight: 500; }
.cat-amt { color: #14663a; font-weight: 700; display: flex; flex-direction: column; align-items: flex-end; line-height: 1.3; }
.cat-amt small { color: #849188; font-size: 9px; font-weight: 600; }

/* 360° Employee Drawer */
.emp-drawer-content { background: #fff; border-radius: 16px; overflow: hidden; display: flex; flex-direction: column; max-height: 88vh; }
.ed-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; background: linear-gradient(135deg, #14663a, #1f7a49); color: #fff; }
.ed-profile { display: flex; align-items: center; gap: 14px; }
.ed-avatar { width: 50px; height: 50px; border-radius: 50%; background: rgba(255,255,255,0.22); display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 800; color: #fff; }
.ed-name { font-size: 18px; font-weight: 700; display: flex; align-items: center; gap: 8px; }
.ed-id { font-size: 13px; font-weight: 500; opacity: 0.85; }
.ed-meta { display: flex; align-items: center; gap: 14px; font-size: 12px; opacity: 0.9; margin-top: 4px; flex-wrap: wrap; }
.ed-meta span { display: inline-flex; align-items: center; gap: 4px; background: rgba(255,255,255,0.15); padding: 2px 8px; border-radius: 10px; }
.ed-close { background: rgba(255,255,255,0.2); border: none; width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; cursor: pointer; }
.ed-close:hover { background: rgba(255,255,255,0.35); }

.ed-body { padding: 20px 24px; overflow-y: auto; flex: 1; }
.drawer-loader { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; padding: 60px 0; color: #6b7a72; font-size: 13px; font-weight: 600; }
.ed-stats-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 20px; }
.ed-stat { background: #f7faf8; border: 1px solid #e2ede5; border-radius: 12px; padding: 12px 14px; text-align: center; }
.ed-stat.pip { background: #eef7f1; border-color: #cfe4d6; }
.eds-v { font-size: 20px; font-weight: 800; color: #14432a; }
.eds-v small { font-size: 12px; font-weight: 600; color: #8a978f; }
.eds-l { font-size: 11px; font-weight: 600; color: #6b7a72; margin-top: 2px; }

.ed-section { margin-top: 18px; }
.ed-sec-title { font-size: 13.5px; font-weight: 700; color: #14432a; display: flex; align-items: center; gap: 6px; margin-bottom: 10px; }
.ed-sec-title.spread { justify-content: space-between; }
.ed-sec-title.spread > span { display: inline-flex; align-items: center; gap: 6px; }
.drawer-link { display: inline-flex; align-items: center; gap: 3px; border: 0; background: transparent; color: #14663a; font: inherit; font-size: 11px; font-weight: 700; cursor: pointer; }
.ed-proj-list { display: flex; gap: 8px; flex-wrap: wrap; }
.ed-proj-chip { font-size: 12px; background: #eef6f0; color: #14663a; border: 1px solid #cfe5d8; padding: 4px 10px; border-radius: 12px; }
.employee-pip-section { background: #f8fbf9; border: 1px solid #e0eae4; border-radius: 12px; padding: 13px 14px; }
.employee-pip-summary { display: grid; grid-template-columns: repeat(6, 1fr); gap: 7px; }
.employee-pip-summary > div { background: #fff; border: 1px solid #e5ede8; border-radius: 9px; padding: 8px 6px; text-align: center; display: flex; flex-direction: column; gap: 3px; }
.employee-pip-summary strong { color: #32483c; font-size: 17px; }
.employee-pip-summary span { color: #829087; font-size: 8.5px; font-weight: 700; }
.pip-project-list { margin-top: 10px; }
.pip-project-list .ed-proj-chip { display: inline-flex; align-items: center; gap: 4px; font-size: 10.5px; }
.pip-project-list .ed-proj-chip.unlinked { color: #8a5b12; background: #fff8eb; border-color: #ead7b2; }
.employee-pip-none { color: #7b8981; background: #fff; border-radius: 9px; padding: 12px; font-size: 11px; }

.ed-table { margin-top: 6px; border: 1px solid #eef2f0; border-radius: 10px; overflow: hidden; }
.ed-footer { padding: 14px 24px; background: #f9fbf9; border-top: 1px solid #edf2ee; display: flex; justify-content: space-between; align-items: center; }
.ed-contact { display: flex; gap: 16px; font-size: 12px; color: #5a6b62; }
.ed-contact span { display: inline-flex; align-items: center; gap: 5px; }

/* Print Layout */
.print-only { display: none; }
@media print {
  .no-print { display: none !important; }
  .print-only { display: block !important; }
  .an-root { background: #fff !important; color: #000 !important; }
  .an-body { padding: 0 !important; max-width: 100% !important; }
  .card { box-shadow: none !important; border: 1px solid #ccc !important; }
  .print-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #14663a; padding-bottom: 12px; margin-bottom: 18px; }
  .print-brand { display: flex; align-items: center; gap: 12px; }
  .print-logo { width: 50px; height: 50px; object-fit: contain; }
  .print-title { font-size: 18px; font-weight: 800; color: #14663a; }
  .print-sub { font-size: 11px; color: #555; }
  .print-date { font-size: 11px; color: #777; }
}

@media (max-width: 900px) {
  .an-body { padding: 16px; }
  .executive-scope { grid-template-columns: repeat(2, 1fr); }
  .scope-intro { grid-column: 1 / -1; }
  .scope-intro, .scope-item { border-bottom: 1px solid #e8efeb; }
  .pip-exec-grid { grid-template-columns: repeat(2, 1fr); }
  .pip-watch-grid { grid-template-columns: 1fr; }
  .talent-methodology, .scorecard-methodology, .pip-methodology, .project-methodology, .yoy-methodology { grid-template-columns: 1fr; }
  .funnel, .matrix-overview-grid, .proj-kpi-grid, .yoy-summary-grid, .grid-2, .ed-stats-grid { grid-template-columns: 1fr; }
  .yoy-comparison-grid { grid-template-columns: repeat(2, 1fr); }
  .yoy-chart-actions { align-items: flex-start; }
  .employee-pip-summary { grid-template-columns: repeat(3, 1fr); }
  .controls { flex-direction: column; align-items: stretch; }
  .matrix-container { flex-direction: column; }
  .matrix-y-axis-label { writing-mode: horizontal-tb; transform: none; margin-bottom: 6px; }
}
@media (max-width: 560px) { .executive-scope, .pip-exec-grid, .yoy-comparison-grid { grid-template-columns: 1fr; } .scope-intro { grid-column: auto; } .employee-pip-summary { grid-template-columns: repeat(2, 1fr); } }
</style>
